// src/pages/LabelDetailPage.js
// Label detail + content editor — route: /labels/:id
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import productLabelService from '../services/productLabelService';
import companyService from '../services/companyService';
import { getNutritionCertificate } from '../services/nutritionService';
import { usePermissions } from '../context/PermissionContext';
import LabelRenderer from '../components/LabelRenderer';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_CONTENT = {
  productName: '',
  netWeight: '',
  upc: '',
  caseUpc: '',
  // Numeric lb weight for the GS1-128 barcode's AI(3202) — content-level base value, same
  // pattern as caseUpc, so it works with no Variant required. See docs/PRODUCT_LABELS_MODULE.md.
  caseNetWeightLb: '',
  // Customer's own product code (e.g. Wild Fork's "PLU") — fixed per product's label content,
  // distinct from the PO number which is entered per print job. See docs/PRODUCT_LABELS_MODULE.md.
  pluSku: '',
  storageInstructions: 'KEEP FROZEN — Store at 0°F (-18°C) or Below',
  cookingInstructions: '',
  ingredients: '',
  allergens: '',
  manufacturerInfo: '',
  countryOfOrigin: '',
  customTexts: {},   // { [blockId]: text }  — overrides block.customText per label
  customImages: {}, // { [blockId]: url }   — overrides block.imageUrl per label
  nutritionFacts: {
    bilingual:            true,
    servingSize:          { en: '', es: '' },
    servingsPerContainer: { en: '', es: '' },
    calories: '',
    totalFat:         { g: '', dv: '' },
    saturatedFat:     { g: '', dv: '' },
    transFat:         { g: '' },
    cholesterol:      { mg: '', dv: '' },
    sodium:           { mg: '', dv: '' },
    totalCarbohydrate:{ g: '', dv: '' },
    dietaryFiber:     { g: '', dv: '' },
    totalSugars:      { g: '' },
    addedSugars:      { g: '', dv: '' },
    protein:          { g: '' },
    vitaminD:         { mcg: '', dv: '' },
    calcium:          { mg: '', dv: '' },
    iron:             { mg: '', dv: '' },
    potassium:        { mg: '', dv: '' },
  },
};

const STATUS_BADGE = {
  draft:            { bg: 'secondary', label: 'Draft' },
  pending_approval: { bg: 'warning',   label: 'Pending Review', text: 'dark' },
  approved:         { bg: 'success',   label: 'Approved' },
  superseded:       { bg: 'secondary', label: 'Superseded', text: 'white' },
  rejected:         { bg: 'danger',    label: 'Rejected' },
  archived:         { bg: 'secondary', label: 'Archived' },
};

// A version is superseded if it was approved but a newer version is now active
function isSuperseded(version, activeVersionId) {
  return version.status === 'approved' && version.id !== activeVersionId;
}

// USDA/FSIS compliance: does this snapshot carry a mark-of-inspection block with an assigned image?
// Mirrors usdaMarkPresent() in backend/routes/productLabelRoutes.js — that route is the real gate,
// this is just used to warn the drafter/reviewer before they hit it.
function usdaMarkPresent(snapshot) {
  return Array.isArray(snapshot?.canvas) &&
    snapshot.canvas.some(b => b.type === 'usda_inspection_mark' && b.imageUrl);
}

// When starting a new draft for a USDA-regulated product, make sure the mark block exists on the
// canvas even if the source design didn't have one — the drafter still has to assign an image to
// it (see AssetPicker), but they don't have to remember to add the block itself.
function ensureUsdaMarkBlock(snapshot, product) {
  if (product?.regulatoryAgency !== 'usda' || !snapshot || !Array.isArray(snapshot.canvas)) return snapshot;
  if (snapshot.canvas.some(b => b.type === 'usda_inspection_mark')) return snapshot;
  const w = 1.2, h = 1.2;
  const labelW = Number(snapshot.width) || 4;
  const mark = {
    id: Math.random().toString(36).slice(2, 9),
    type: 'usda_inspection_mark',
    x: Math.max(0, labelW - w - 0.1), y: 0.1, w, h,
  };
  return { ...snapshot, canvas: [...snapshot.canvas, mark] };
}

// Small inline select to swap the label design on a draft version
function ChangeDesignSelect({ templateId, versionId, onChanged }) {
  const [designs, setDesigns] = useState([]);
  useEffect(() => {
    productLabelService.getDesigns().then(setDesigns).catch(() => {});
  }, []);
  if (designs.length === 0) return null;
  return (
    <select
      className="form-select form-select-sm"
      style={{ fontSize: 11, width: 'auto', maxWidth: 180 }}
      value=""
      onChange={e => { if (e.target.value) onChanged(versionId, e.target.value); }}
      title="Change label design template"
    >
      <option value="">Change design…</option>
      {designs.map(d => (
        <option key={d.id} value={d.id}>{d.name}</option>
      ))}
    </select>
  );
}

const EXP_LABEL_DISPLAY = {
  best_by: 'Best By', use_by: 'Use By', exp: 'Exp.',
  best_if_used_by: 'Best If Used By', custom: 'Custom',
};

// Resolve a template's expiration-label display text. For 'custom', use the operator-entered
// text (expirationLabelEn) rather than the literal word "Custom" — falls back to it only if
// the custom text was never filled in.
function resolveExpLabel(template) {
  if (!template) return EXP_LABEL_DISPLAY.best_by;
  if (template.expirationLabel === 'custom') {
    return (template.expirationLabelEn || '').trim() || EXP_LABEL_DISPLAY.custom;
  }
  return EXP_LABEL_DISPLAY[template.expirationLabel] || EXP_LABEL_DISPLAY.best_by;
}

// ─── Inline Canvas Editor — shared constants & components ─────────────────────

const CANVAS_BLOCK_COLORS = {
  product_name:'#2563eb', net_weight:'#7c3aed', brand_logo:'#0891b2',
  storage_banner:'#dc2626', cooking_instructions:'#0d9488', ingredients:'#16a34a',
  allergen_statement:'#d97706', nutrition_facts:'#9333ea', upc_barcode:'#475569',
  upc_text:'#64748b', lot_exp_box:'#0f766e', qr_code:'#2563eb',
  manufacturer_info:'#92400e', country_of_origin:'#6b7280',
  custom_text:'#b45309', custom_image:'#be185d', usda_inspection_mark:'#b91c1c', divider:'#94a3b8',
};

const snapL  = (v, g = 0.05) => Math.round(v / g) * g;
const clampL = (v, min, max) => Math.min(Math.max(v, min), max);

// Convert old columns/rows snapshot to flat canvas blocks
function convertSnapshotToCanvas(snapshot) {
  const W = snapshot?.width || 6;
  const H = snapshot?.height || 4;
  const uid = () => Math.random().toString(36).slice(2, 9);
  const blocks = [];

  const collectBlocks = (list, x, y, w, h) => {
    (list || []).forEach((b, i, arr) => {
      const bh = h / (arr.length || 1);
      blocks.push({ ...b, id: b.id || uid(), x: snapL(x), y: snapL(y + i * bh), w: snapL(w), h: snapL(bh) });
    });
  };

  if (Array.isArray(snapshot?.canvas)) return snapshot.canvas;

  if (Array.isArray(snapshot?.columns) && snapshot.columns.length) {
    const totalFlex = snapshot.columns.reduce((s, p) => s + (p.flex || 1), 0);
    let px = 0;
    for (const panel of snapshot.columns) {
      const pw = ((panel.flex || 1) / totalFlex) * W;
      if (panel.rows?.length) {
        const trf = panel.rows.reduce((s, r) => s + (r.flex || 1), 0);
        let ry = 0;
        for (const row of panel.rows) {
          const rh = ((row.flex || 1) / trf) * H;
          const cols = row.columns || [];
          const tcf = cols.reduce((s, c) => s + (c.flex || 1), 0) || 1;
          let cx = px;
          for (const col of cols) {
            const cw = ((col.flex || 1) / tcf) * pw;
            collectBlocks(col.blocks, cx, ry, cw, rh);
            cx += cw;
          }
          ry += rh;
        }
      } else {
        collectBlocks(panel.blocks, px, 0, pw, H);
      }
      px += pw;
    }
  } else if (Array.isArray(snapshot?.rows) && snapshot.rows.length) {
    const trf = snapshot.rows.reduce((s, r) => s + (r.flex || 1), 0);
    let ry = 0;
    for (const row of snapshot.rows) {
      const rh = ((row.flex || 1) / trf) * H;
      const cols = row.columns || [];
      const tcf = cols.reduce((s, c) => s + (c.flex || 1), 0) || 1;
      let cx = 0;
      for (const col of cols) {
        const cw = ((col.flex || 1) / tcf) * W;
        collectBlocks(col.blocks, cx, ry, cw, rh);
        cx += cw;
      }
      ry += rh;
    }
  }
  return blocks;
}

// Per-block drag+resize overlay (no add/delete — just move & resize)
function InlineBlockOverlay({ block, scale, isSelected, onMouseDownMove, onMouseDownResize, onClick }) {
  const PPI  = 96;
  const left   = block.x * PPI * scale;
  const top    = block.y * PPI * scale;
  const width  = Math.max(block.w * PPI * scale, 16);
  const height = Math.max(block.h * PPI * scale, 8);
  const color  = CANVAS_BLOCK_COLORS[block.type] || '#666';

  return (
    <div
      style={{
        position: 'absolute', left, top, width, height,
        border: isSelected ? `2px solid ${color}` : `1px dashed ${color}88`,
        backgroundColor: isSelected ? `${color}1a` : `${color}08`,
        cursor: 'move', boxSizing: 'border-box',
        zIndex: isSelected ? 20 : 10, userSelect: 'none',
      }}
      onMouseDown={onMouseDownMove}
      onClick={e => { e.stopPropagation(); onClick(); }}
    >
      {/* Type label */}
      <div style={{
        position: 'absolute', top: 1, left: 2,
        background: color, color: 'white',
        fontSize: Math.max(7, 9 * scale), padding: '1px 3px',
        borderRadius: 2, lineHeight: 1.3, pointerEvents: 'none',
        maxWidth: 'calc(100% - 4px)', overflow: 'hidden', whiteSpace: 'nowrap', opacity: 0.85,
      }}>
        {block.type.replace(/_/g, ' ')}
      </div>
      {/* SE resize handle */}
      {isSelected && width > 24 && height > 14 && (
        <div
          style={{
            position: 'absolute', bottom: -5, right: -5,
            width: 14, height: 14, background: color,
            border: '2px solid white', cursor: 'se-resize',
            borderRadius: 3, zIndex: 30, boxShadow: '0 1px 4px rgba(0,0,0,.3)',
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e, 'se'); }}
        />
      )}
      {/* E resize handle */}
      {isSelected && height > 24 && (
        <div
          style={{
            position: 'absolute', top: '50%', right: -4,
            width: 8, height: 18, background: color,
            border: '2px solid white', cursor: 'e-resize',
            borderRadius: 3, transform: 'translateY(-50%)', zIndex: 30,
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e, 'e'); }}
        />
      )}
      {/* S resize handle */}
      {isSelected && width > 40 && (
        <div
          style={{
            position: 'absolute', bottom: -4, left: '50%',
            width: 18, height: 8, background: color,
            border: '2px solid white', cursor: 's-resize',
            borderRadius: 3, transform: 'translateX(-50%)', zIndex: 30,
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e, 's'); }}
        />
      )}
    </div>
  );
}

// Block types that support text styling
const TEXT_BLOCK_TYPES = new Set([
  'product_name','net_weight','storage_banner','cooking_instructions','ingredients',
  'allergen_statement','manufacturer_info','country_of_origin','custom_text','upc_text','lot_exp_box',
]);

// Inline layout editor embedded in the label detail page
function InlineCanvasEditor({ snapshot, templateId, versionId, onSaved }) {
  const [blocks, setBlocks]           = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState('');
  const containerRef                  = useRef(null);
  const [containerW, setContainerW]   = useState(600);
  const dragRef                       = useRef(null);
  const scaleRef                      = useRef(1);
  const dimsRef                       = useRef({ w: 6, h: 4 });

  // Asset library
  const [assets, setAssets]           = useState([]);
  const [assetUploading, setAssetUploading] = useState(false);
  const [assetUploadErr, setAssetUploadErr] = useState('');
  const assetFileRef                  = useRef(null);

  useEffect(() => {
    productLabelService.getAssets().then(setAssets).catch(() => {});
  }, []);

  const labelW = snapshot?.width  || 6;
  const labelH = snapshot?.height || 4;
  const scale  = Math.min(1.2, Math.max(0.2, (containerW - 8) / (labelW * 96)));

  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { dimsRef.current = { w: labelW, h: labelH }; }, [labelW, labelH]);

  // Init blocks from snapshot
  useEffect(() => {
    setBlocks(convertSnapshotToCanvas(snapshot));
    setSelectedId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  // Global drag listeners (reads only from refs — no stale closure)
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { blockId, mode, startX, startY, origX, origY, origW, origH } = dragRef.current;
      const s = scaleRef.current;
      const { w: lW, h: lH } = dimsRef.current;
      const dx = (e.clientX - startX) / (96 * s);
      const dy = (e.clientY - startY) / (96 * s);
      setBlocks(prev => prev.map(b => {
        if (b.id !== blockId) return b;
        if (mode === 'move') return { ...b, x: snapL(clampL(origX + dx, 0, lW - b.w)), y: snapL(clampL(origY + dy, 0, lH - b.h)) };
        if (mode === 'se')   return { ...b, w: snapL(clampL(origW + dx, 0.15, lW - b.x)), h: snapL(clampL(origH + dy, 0.05, lH - b.y)) };
        if (mode === 'e')    return { ...b, w: snapL(clampL(origW + dx, 0.15, lW - b.x)) };
        if (mode === 's')    return { ...b, h: snapL(clampL(origH + dy, 0.05, lH - b.y)) };
        return b;
      }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  // Observe container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => { for (const entry of entries) setContainerW(entry.contentRect.width); });
    ro.observe(el);
    setContainerW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const startDrag = useCallback((e, block, mode) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedId(block.id);
    dragRef.current = { blockId: block.id, mode, startX: e.clientX, startY: e.clientY, origX: block.x, origY: block.y, origW: block.w, origH: block.h };
  }, []);

  const selectedBlock = useMemo(() => blocks.find(b => b.id === selectedId) || null, [blocks, selectedId]);

  const handleSave = async () => {
    setSaving(true); setSaveMsg('');
    try {
      await productLabelService.updateVersion(templateId, versionId, {
        snapshot: { ...snapshot, canvas: blocks },
      });
      setSaveMsg('Saved!');
      onSaved();
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
      setSaveMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ALLOWED = ['image/jpeg','image/jpg','image/png','image/webp','image/gif','image/svg+xml'];
    if (!ALLOWED.includes(file.type)) { setAssetUploadErr('Only JPEG, PNG, WebP, GIF, SVG allowed.'); return; }
    if (file.size > 5 * 1024 * 1024) { setAssetUploadErr('Max 5 MB.'); return; }
    setAssetUploadErr(''); setAssetUploading(true);
    try {
      const { uploadUrl, publicUrl, key } = await productLabelService.getAssetUploadUrl(file.name, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const asset = await productLabelService.createAsset({ name, fileUrl: publicUrl, s3Key: key, fileSize: file.size, mimeType: file.type });
      setAssets(prev => [asset, ...prev]);
      setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, imageUrl: asset.fileUrl, imageName: asset.name } : b));
    } catch { setAssetUploadErr('Upload failed.'); }
    finally { setAssetUploading(false); e.target.value = ''; }
  };

  const canvasPxW = labelW * 96 * scale;
  const canvasPxH = labelH * 96 * scale;
  const canvasDesign = useMemo(() => ({ width: labelW, height: labelH, canvas: blocks }), [labelW, labelH, blocks]);

  return (
    <div>
      {/* Toolbar */}
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="text-muted" style={{ fontSize: 11 }}>
          Drag blocks to reposition · Corner/edge handles to resize · Click to select
        </span>
        <div className="ms-auto d-flex align-items-center gap-2">
          {saveMsg && (
            <span className={`small ${saveMsg === 'Saved!' ? 'text-success' : 'text-danger'}`}>{saveMsg}</span>
          )}
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Layout Changes'}
          </button>
        </div>
      </div>

      {/* Selected block info — always rendered at fixed height so canvas never jumps */}
      {(() => {
        const sb = selectedBlock;
        const st = sb?.style || {};
        const isText = sb ? TEXT_BLOCK_TYPES.has(sb.type) : false;
        const updateStyle = (patch) => setBlocks(prev => prev.map(b =>
          b.id === selectedId ? { ...b, style: { ...(b.style || {}), ...patch } } : b
        ));
        return (
          <div className="d-flex flex-column gap-1 mb-2" style={{ minHeight: 72 }}>
            {/* Position / size row */}
            <div
              className="d-flex align-items-center gap-2 px-2 py-1 rounded border bg-white flex-wrap"
              style={{ fontSize: 11, visibility: sb ? 'visible' : 'hidden' }}
            >
              <span style={{ color: CANVAS_BLOCK_COLORS[sb?.type] || '#333', fontWeight: 600 }}>
                {sb ? sb.type.replace(/_/g, ' ') : 'block'}
              </span>
              <span className="text-muted">·</span>
              {['x','y','w','h'].map(k => (
                <label key={k} className="d-flex align-items-center gap-1 mb-0">
                  <span className="text-muted" style={{ minWidth: 12 }}>{k.toUpperCase()}</span>
                  <input
                    type="number" step={0.05} min={k === 'w' ? 0.15 : k === 'h' ? 0.05 : 0}
                    className="form-control form-control-sm"
                    style={{ width: 60 }}
                    value={sb ? Number(sb[k]).toFixed(2) : ''}
                    readOnly={!sb}
                    onChange={e => {
                      if (!sb) return;
                      const val = parseFloat(e.target.value) || 0;
                      const clamped = k === 'w' ? Math.max(0.15, val) : k === 'h' ? Math.max(0.05, val) : Math.max(0, val);
                      setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, [k]: snapL(clamped) } : b));
                    }}
                  />
                  <span className="text-muted">"</span>
                </label>
              ))}
              <button
                className="btn btn-link btn-sm p-0 text-danger ms-auto"
                onClick={() => { setBlocks(prev => prev.filter(b => b.id !== selectedId)); setSelectedId(null); }}
                title="Remove block from layout"
                disabled={!sb}
              >
                Remove
              </button>
            </div>
            {/* Text style row — always rendered, hidden for non-text blocks */}
            <div
              className="d-flex align-items-center gap-2 px-2 py-1 rounded border bg-white flex-wrap"
              style={{ fontSize: 11, visibility: (sb && isText) ? 'visible' : 'hidden' }}
            >
              <span className="text-muted" style={{ fontSize: 10 }}>Style:</span>
              <label className="d-flex align-items-center gap-1 mb-0">
                <span className="text-muted">Size</span>
                <input
                  type="number" step={1} min={5} max={72}
                  className="form-control form-control-sm"
                  style={{ width: 55 }}
                  value={st.fontSize || ''}
                  placeholder="auto"
                  onChange={e => updateStyle({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
                />
              </label>
              <button
                className={`btn btn-sm ${st.fontWeight === 'bold' ? 'btn-dark' : 'btn-outline-secondary'}`}
                style={{ minWidth: 30, fontWeight: 'bold', padding: '1px 7px' }}
                onClick={() => updateStyle({ fontWeight: st.fontWeight === 'bold' ? undefined : 'bold' })}
                title="Bold"
              >B</button>
              <button
                className={`btn btn-sm ${st.fontStyle === 'italic' ? 'btn-dark' : 'btn-outline-secondary'}`}
                style={{ minWidth: 30, fontStyle: 'italic', padding: '1px 7px' }}
                onClick={() => updateStyle({ fontStyle: st.fontStyle === 'italic' ? undefined : 'italic' })}
                title="Italic"
              >I</button>
              {['left','center','right'].map(align => (
                <button
                  key={align}
                  className={`btn btn-sm ${st.textAlign === align ? 'btn-dark' : 'btn-outline-secondary'}`}
                  style={{ padding: '1px 7px' }}
                  onClick={() => updateStyle({ textAlign: st.textAlign === align ? undefined : align })}
                  title={align.charAt(0).toUpperCase() + align.slice(1)}
                >
                  {align === 'left' ? '≡' : align === 'center' ? '☰' : '≡'}
                  <span style={{ fontSize: 9, marginLeft: 2 }}>{align[0].toUpperCase()}</span>
                </button>
              ))}
            </div>
            {/* lot_exp_box block properties row */}
            {sb?.type === 'lot_exp_box' && (
              <div className="d-flex align-items-center gap-3 px-2 py-1 rounded border bg-white flex-wrap" style={{ fontSize: 11 }}>
                <span className="text-muted" style={{ fontSize: 10 }}>Lot/Exp options:</span>
                <label className="d-flex align-items-center gap-1 mb-0">
                  <input
                    type="checkbox"
                    checked={sb.showProdDate === true}
                    onChange={e => setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, showProdDate: e.target.checked } : b))}
                  />
                  <span>Show Production Date</span>
                </label>
                {sb.showProdDate && (
                  <label className="d-flex align-items-center gap-1 mb-0">
                    <span className="text-muted">Label</span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: 80 }}
                      value={sb.prodDateLabel ?? 'P.'}
                      placeholder="P."
                      onChange={e => setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, prodDateLabel: e.target.value } : b))}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Canvas + image library sidebar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {/* Canvas — flex: 1 so it shrinks to make room for the sidebar */}
        <div ref={containerRef} style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ position: 'relative', width: canvasPxW, height: canvasPxH, margin: '0 auto', boxShadow: '0 2px 16px rgba(0,0,0,.15)', cursor: 'default' }}
            onClick={() => setSelectedId(null)}
          >
            <LabelRenderer design={canvasDesign} scale={scale} isDesign={true} />
            <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
              {blocks.map(block => (
                <InlineBlockOverlay
                  key={block.id}
                  block={block}
                  scale={scale}
                  isSelected={selectedId === block.id}
                  onMouseDownMove={e => startDrag(e, block, 'move')}
                  onMouseDownResize={(e, dir) => startDrag(e, block, dir)}
                  onClick={() => setSelectedId(block.id)}
                />
              ))}
              {blocks.length === 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13, pointerEvents: 'none' }}>
                  No blocks — go to Label Designs to set up the layout first
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Asset picker sidebar — always takes space; hidden when not a custom_image block */}
        {(() => {
          const isImageBlock = selectedBlock?.type === 'custom_image' || selectedBlock?.type === 'usda_inspection_mark';
          const currentUrl = selectedBlock?.imageUrl || '';
          return (
            <div
              className="border rounded bg-white"
              style={{ width: 156, flexShrink: 0, visibility: isImageBlock ? 'visible' : 'hidden',
                height: canvasPxH, display: 'flex', flexDirection: 'column', fontSize: 11, overflow: 'hidden' }}
            >
              <div className="d-flex align-items-center gap-1 px-2 pt-2 pb-1 border-bottom flex-wrap">
                <span className="text-muted fw-semibold" style={{ fontSize: 10 }}>Images</span>
                <button
                  className="btn btn-outline-primary btn-sm py-0 px-1 ms-auto"
                  style={{ fontSize: 10 }}
                  onClick={() => assetFileRef.current?.click()}
                  disabled={assetUploading}
                >
                  {assetUploading ? '…' : '+ Upload'}
                </button>
                <input ref={assetFileRef} type="file" accept="image/*" className="d-none" onChange={handleAssetUpload} />
              </div>
              {assetUploadErr && <div className="text-danger px-2 pt-1" style={{ fontSize: 10 }}>{assetUploadErr}</div>}
              <div style={{ flex: 1, overflowY: 'auto', padding: 6, display: 'flex', flexWrap: 'wrap', gap: 5, alignContent: 'flex-start' }}>
                {assets.length === 0 && !assetUploading && (
                  <span className="text-muted" style={{ fontSize: 10 }}>No images yet.</span>
                )}
                {assets.map(asset => {
                  const isSel = currentUrl === asset.fileUrl;
                  return (
                    <div
                      key={asset.id}
                      onClick={() => isImageBlock && setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, imageUrl: asset.fileUrl, imageName: asset.name } : b))}
                      title={asset.name}
                      style={{
                        width: 62, height: 62, border: isSel ? '2px solid #be185d' : '1px solid #dee2e6',
                        borderRadius: 4, cursor: 'pointer', overflow: 'hidden',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSel ? '#fdf2f8' : '#fafafa', flexShrink: 0,
                      }}
                    >
                      <img src={asset.fileUrl} alt={asset.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { bg: 'light', label: status, text: 'dark' };
  return <span className={`badge bg-${cfg.bg} text-${cfg.text || 'white'}`}>{cfg.label}</span>;
}

function formatDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  catch { return str; }
}

// Extract design + content from a snapshot
function splitSnapshot(snapshot) {
  if (!snapshot) return { design: null, content: {} };
  const { content = {}, ...design } = snapshot;
  return { design, content };
}

// Format an ISO date string (YYYY-MM-DD or full ISO) according to a dateFormat token
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// CFIA bilingual month codes for date marking (Food and Drug Regulations date-marking rules) —
// e.g. "10 SE 26" for September 10.
const CA_MONTH_CODES = ['JA','FE','MR','AL','MA','JN','JL','AU','SE','OC','NO','DE'];
function formatLabelDate(str, fmt) {
  if (!str) return '';
  try {
    // parse only the date portion to avoid timezone shifts
    const [y, m, d] = str.slice(0, 10).split('-');
    if (!y || !m || !d) return str;
    if (fmt === 'DD/MM/YYYY') return `${d}/${m}/${y}`;
    if (fmt === 'YYYY-MM-DD') return `${y}-${m}-${d}`;
    if (fmt === 'DD-MMM-YYYY') return `${d}-${MONTH_ABBR[parseInt(m, 10) - 1]}-${y}`;
    if (fmt === 'DD-MON-YY-CA') return `${d} ${CA_MONTH_CODES[parseInt(m, 10) - 1]} ${y.slice(-2)}`;
    return `${m}/${d}/${y}`; // MM/DD/YYYY (default)
  } catch { return str; }
}

// GS1-128 AI(21) serial — lot code + a per-case sequence number within this print run, per
// WFF's own suggested format ("lot code plus a serial number"). Alphanumeric + hyphen only,
// truncated to GS1's 20-character max for AI 21.
function buildGs1Serial(lotCode, seq) {
  const clean = (lotCode || '').toString().replace(/[^A-Za-z0-9-]/g, '');
  const suffix = `-${String(seq).padStart(3, '0')}`;
  return (clean + suffix).slice(0, 20);
}

// ─── Live Preview ─────────────────────────────────────────────────────────────

function ScaledPreview({ snapshot, logoUrl, template }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  if (!snapshot) {
    return (
      <div className="text-muted text-center py-5" style={{ fontSize: 13 }}>
        <div style={{ fontSize: 32 }}>🏷️</div>
        <p className="mt-2 mb-0">No design snapshot yet.<br />Create a new version with a label design to see a preview.</p>
      </div>
    );
  }

  const { design, content } = splitSnapshot(snapshot);
  const pxWidth = (design?.width || 6) * 96;
  const scale = Math.min(1, (containerWidth - 16) / pxWidth);

  // Simulate the fields that are normally only computed at actual print time (see the
  // baseContent construction in the print portal below), using the template's own settings
  // and placeholder sample data. Without this, the preview always falls back to LabelRenderer's
  // hardcoded "BEST BY:" / "Exp.: ______" text no matter what Expiration Label / Date Format
  // are configured, making those settings look like they have no effect.
  const expLabel = resolveExpLabel(template);
  const dateFmt = template?.dateFormat || 'MM/DD/YYYY';
  const previewExpDate = formatLabelDate('2026-12-31', dateFmt);
  const previewProdDate = formatLabelDate('2026-01-01', dateFmt);
  const derivedContent = {
    expirationLabel: expLabel + ':',
    lot: 'Lot: 123456',
    exp: `${expLabel}: ${previewExpDate}`,
    prodDate: previewProdDate,
    po: 'PO 8038',
  };
  const contentWithLogo = {
    ...content,
    ...(logoUrl && { logoUrl }),
    ...derivedContent,
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <LabelRenderer design={design} content={contentWithLogo} scale={scale} isDesign={false} />
    </div>
  );
}

// ─── Reject Modal ─────────────────────────────────────────────────────────────

function RejectModal({ show, onClose, onConfirm, loading }) {
  const [notes, setNotes] = useState('');
  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Reject Version</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
            </div>
            <div className="modal-body">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                Rejection Notes <span className="text-muted fw-normal">(optional)</span>
              </label>
              <textarea className="form-control form-control-sm" rows={3} value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Explain why this version is being rejected…" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => { onConfirm(notes); setNotes(''); }} disabled={loading}>
                {loading ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Rejecting…</> : 'Reject Version'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── New Version Modal ────────────────────────────────────────────────────────

function NewVersionModal({ show, onClose, template, onCreated }) {
  const [changeNotes, setChangeNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState('');

  useEffect(() => {
    if (show) productLabelService.getDesigns().then(setDesigns).catch(() => {});
  }, [show]);

  const handleClose = () => { setChangeNotes(''); setError(''); setSelectedDesignId(''); onClose(); };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const versions = (template.Versions || []).slice().sort((a, b) => b.versionNumber - a.versionNumber);
      const source = versions.find(v => v.id === template.activeVersionId) || versions[0];

      let snapshot = source?.snapshot || null;

      if (selectedDesignId) {
        const design = designs.find(d => String(d.id) === String(selectedDesignId));
        if (design) {
          const layout = design.rows || {};
          // Preserve content from previous version, replace layout
          const prevContent = source?.snapshot?.content || {};
          snapshot = { ...layout, width: design.width, height: design.height, content: prevContent };
        }
      }

      const newVersion = await productLabelService.createVersion(template.id, {
        snapshot: ensureUsdaMarkBlock(snapshot, template.Product),
        changeNotes: changeNotes.trim() || null,
      });
      onCreated(newVersion);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create version.');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const versions = (template?.Versions || []).slice().sort((a, b) => b.versionNumber - a.versionNumber);
  const source = versions.find(v => v.id === template.activeVersionId) || versions[0];
  const nextNum = versions.length ? Math.max(...versions.map(v => v.versionNumber)) + 1 : 1;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={handleClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New Version (v{nextNum})</h5>
              <button type="button" className="btn-close" onClick={handleClose} disabled={saving} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              {/* Design source */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Label Design</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedDesignId}
                  onChange={e => setSelectedDesignId(e.target.value)}
                >
                  <option value="">
                    {source ? `Keep current design (from v${source.versionNumber})` : 'No design — blank layout'}
                  </option>
                  {designs.map(d => (
                    <option key={d.id} value={d.id}>{d.name} ({d.width}×{d.height}")</option>
                  ))}
                </select>
                <div className="form-text" style={{ fontSize: 11 }}>
                  {selectedDesignId
                    ? 'New layout will be used. Label content will be copied from the previous version.'
                    : source
                      ? `Layout copied from v${source.versionNumber}. Content also copied.`
                      : 'A blank draft will be created.'}
                </div>
              </div>

              <div>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Change Notes <span className="text-muted fw-normal">(optional)</span>
                </label>
                <textarea className="form-control form-control-sm" rows={2} value={changeNotes}
                  onChange={e => setChangeNotes(e.target.value)}
                  placeholder="Describe what changed in this version…" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Creating…</> : `Create v${nextNum}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Print Modal ──────────────────────────────────────────────────────────────

const MAX_DROPDOWN_LOTS = 20;

function PrintModal({ show, onClose, template, variants = [], onConfirm, confirming, confirmError, prefillOrderId }) {
  const today = new Date().toISOString().split('T')[0];
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [lotCode, setLotCode] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lotWarning, setLotWarning] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [customerOrderId, setCustomerOrderId] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [productionDate, setProductionDate] = useState(today);
  const [expirationDate, setExpirationDate] = useState('');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [notes, setNotes] = useState('');
  const [copies, setCopies] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const lotInputRef = useRef(null);

  // Sync dateFormat from template whenever modal opens
  useEffect(() => {
    if (show) setDateFormat(template?.dateFormat || 'MM/DD/YYYY');
  }, [show, template?.dateFormat]);

  // Restore last-used variant from sessionStorage
  // 'base' = label content as-is (no overrides), a numeric id = specific variant
  useEffect(() => {
    if (show && variants.length > 0) {
      const stored = sessionStorage.getItem(`lastVariant_${template?.id}`);
      if (stored === 'base') {
        setSelectedVariantId(null);
      } else if (stored) {
        const storedId = parseInt(stored, 10);
        const match = variants.find(v => v.id === storedId);
        if (match) {
          setSelectedVariantId(storedId);
        } else {
          // Stored variant no longer exists — fall back to isDefault or null
          const def = variants.find(v => v.isDefault);
          setSelectedVariantId(def?.id ?? null);
        }
      } else {
        // No stored preference — default to isDefault variant if one exists
        const def = variants.find(v => v.isDefault);
        setSelectedVariantId(def?.id ?? null);
      }
    } else {
      setSelectedVariantId(null);
    }
  }, [show, template?.id, variants]);

  // Fetch lot codes when modal opens
  useEffect(() => {
    if (!show || !template?.productId) return;
    setLoadingLots(true);
    productLabelService.getLotCodes(template.productId)
      .then(setLots)
      .catch(() => setLots([]))
      .finally(() => setLoadingLots(false));
  }, [show, template?.productId]);

  // Fetch open orders for the optional PO/order picker. Auto-select when there's exactly one
  // match (zero clicks in the common case), otherwise restore the last order picked for this
  // label template in this browser session — same reasoning as the variant picker's session
  // memory: printing several case labels back-to-back for the same PO shouldn't mean re-picking
  // it every single time. Neither ever overrides a PO the user already typed manually.
  useEffect(() => {
    if (!show || !template?.productId) return;
    setLoadingOrders(true);
    productLabelService.getOpenOrders(template.productId)
      .then(orders => {
        setOpenOrders(orders);
        setPoNumber(prevPo => {
          if (prevPo) return prevPo; // don't clobber something already typed/selected
          let picked = null;
          // Order chosen up front in the Quick Print flow always wins over the "only one
          // match" / session-memory heuristics below.
          if (prefillOrderId) {
            picked = orders.find(o => String(o.id) === String(prefillOrderId)) || null;
          }
          if (!picked && orders.length === 1) {
            picked = orders[0];
          } else if (!picked && orders.length > 1) {
            const storedId = sessionStorage.getItem(`lastOrder_${template.id}`);
            if (storedId) picked = orders.find(o => String(o.id) === storedId) || null;
          }
          if (picked) {
            setCustomerOrderId(picked.id);
            return picked.poNumber || picked.orderNumber || '';
          }
          return prevPo;
        });
      })
      .catch(() => setOpenOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [show, template?.productId, template?.id, prefillOrderId]);

  // Auto-calculate expiration from production date + shelf life
  useEffect(() => {
    if (productionDate && template?.Product?.shelfLifeDays) {
      const d = new Date(productionDate + 'T00:00:00');
      d.setDate(d.getDate() + template.Product.shelfLifeDays);
      setExpirationDate(d.toISOString().split('T')[0]);
    }
  }, [productionDate, template?.Product?.shelfLifeDays]);

  // When a lot is selected from the capped dropdown
  const handleLotSelect = (e) => {
    const selected = lots.find(l => l.lotCode === e.target.value);
    if (!selected) return;
    setLotCode(selected.lotCode);
    setLotWarning('');
    setSuggestions([]);
    if (selected.productionDate) setProductionDate(selected.productionDate);
  };

  // Live typeahead filtering
  const handleLotCodeChange = (e) => {
    const val = e.target.value;
    setLotCode(val);
    setLotWarning('');
    if (val.length >= 2) {
      const matches = lots.filter(l =>
        l.lotCode.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(matches.slice(0, 10));
      setShowSuggestions(matches.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Pick a suggestion from autocomplete list
  const handleSuggestionPick = (lot) => {
    setLotCode(lot.lotCode);
    setLotWarning('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (lot.productionDate) setProductionDate(lot.productionDate);
    lotInputRef.current?.focus();
  };

  // Validate on blur — warn if entered value not in the loaded lot list
  const handleLotBlur = () => {
    setShowSuggestions(false);
    if (!lotCode.trim() || lots.length === 0) return;
    const found = lots.some(l => l.lotCode.toLowerCase() === lotCode.trim().toLowerCase());
    if (!found) {
      const productName = template?.Product?.name || 'this product';
      setLotWarning(`No "${productName}" produced in lot ${lotCode.trim()}`);
    }
  };

  // Selecting an order from the dropdown links this print job to it and auto-fills the PO field.
  const handleOrderSelect = (e) => {
    const id = e.target.value;
    if (!id) {
      setCustomerOrderId(null);
      sessionStorage.removeItem(`lastOrder_${template?.id}`);
      return;
    }
    const order = openOrders.find(o => String(o.id) === id);
    if (!order) return;
    setCustomerOrderId(order.id);
    setPoNumber(order.poNumber || order.orderNumber || '');
    sessionStorage.setItem(`lastOrder_${template?.id}`, String(order.id));
  };

  // Typing directly in the PO field means it may no longer match the picked order — clear the
  // link rather than leave a stale/misleading one. Only the dropdown sets both together.
  const handlePoNumberChange = (e) => {
    setPoNumber(e.target.value);
    setCustomerOrderId(null);
  };

  const handleClose = () => {
    setLotCode(''); setProductionDate(today); setExpirationDate('');
    setDateFormat('MM/DD/YYYY'); setNotes(''); setCopies(1); setLots([]);
    setSuggestions([]); setShowSuggestions(false); setLotWarning('');
    setSelectedVariantId(null);
    setPoNumber(''); setCustomerOrderId(null); setOpenOrders([]);
    onClose();
  };

  const handleVariantSelect = (variantId) => {
    setSelectedVariantId(variantId);
    sessionStorage.setItem(`lastVariant_${template?.id}`, variantId === null ? 'base' : variantId);
  };

  if (!show) return null;

  // Cap dropdown to MAX_DROPDOWN_LOTS (in-progress first, then newest completed)
  const inProgress = lots.filter(l => l.status === 'in_progress');
  const completed  = lots.filter(l => l.status === 'completed');
  const dropdownCompleted = completed.slice(0, Math.max(0, MAX_DROPDOWN_LOTS - inProgress.length));
  const showDropdown = lots.length > 0;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={handleClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Print Label — {template?.name}</h5>
              <button type="button" className="btn-close" onClick={handleClose} disabled={confirming} />
            </div>
            <div className="modal-body">
              {confirmError && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{confirmError}</div>}

              {/* Variant / Presentation picker */}
              {variants.length > 0 && (
                <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                  <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Presentation</div>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Base option — label as-is, no overrides */}
                    <button
                      type="button"
                      className={`btn btn-sm ${selectedVariantId === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleVariantSelect(null)}
                    >
                      {template?.baseVariantName || 'Base Label'}
                    </button>
                    {variants.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        className={`btn btn-sm ${selectedVariantId === v.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handleVariantSelect(v.id)}
                      >
                        {v.name}
                        {v.isDefault && selectedVariantId !== v.id && (
                          <span className="ms-1 text-muted" style={{ fontSize: 10 }}>(default)</span>
                        )}
                      </button>
                    ))}
                  </div>
                  {selectedVariantId && (() => {
                    const v = variants.find(x => x.id === selectedVariantId);
                    if (!v) return null;
                    const overrides = [
                      v.labelTitle && `Title: ${v.labelTitle}`,
                      v.netWeight && `Net wt: ${v.netWeight}`,
                      v.upc && `UPC: ${v.upc}`,
                    ].filter(Boolean);
                    return overrides.length > 0 ? (
                      <div className="mt-2 text-muted" style={{ fontSize: 11 }}>{overrides.join(' · ')}</div>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Lot code selector */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Lot Code</label>
                {loadingLots ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />Loading lots…
                  </div>
                ) : showDropdown ? (
                  <select className="form-select form-select-sm mb-2"
                    value=""
                    onChange={handleLotSelect}>
                    <option value="">— Quick select (recent {Math.min(lots.length, MAX_DROPDOWN_LOTS)}) —</option>
                    {inProgress.length > 0 && (
                      <optgroup label="⚙ In Progress">
                        {inProgress.map(l => (
                          <option key={l.lotCode} value={l.lotCode}>{l.lotCode}</option>
                        ))}
                      </optgroup>
                    )}
                    {dropdownCompleted.length > 0 && (
                      <optgroup label="✓ Completed">
                        {dropdownCompleted.map(l => (
                          <option key={l.lotCode} value={l.lotCode}>{l.lotCode}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                ) : null}

                {/* Smart search input with autocomplete */}
                <div style={{ position: 'relative' }}>
                  <input
                    ref={lotInputRef}
                    type="text"
                    className={`form-control form-control-sm${lotWarning ? ' is-warning' : ''}`}
                    placeholder={showDropdown ? 'or search / type a lot code…' : 'Type a lot code…'}
                    value={lotCode}
                    onChange={handleLotCodeChange}
                    onBlur={handleLotBlur}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1100,
                      listStyle: 'none', margin: 0, padding: 0,
                      background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px',
                      boxShadow: '0 4px 8px rgba(0,0,0,.1)', maxHeight: 220, overflowY: 'auto',
                    }}>
                      {suggestions.map(l => (
                        <li key={l.lotCode}
                          onMouseDown={() => handleSuggestionPick(l)}
                          style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <span className="fw-semibold">{l.lotCode}</span>
                          {l.status === 'in_progress' && (
                            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>In Progress</span>
                          )}
                          {l.productionDate && (
                            <span className="text-muted ms-2" style={{ fontSize: 11 }}>{l.productionDate}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {lotWarning && (
                  <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: '#b45309' }}>
                    <span>⚠</span> {lotWarning}
                  </div>
                )}
              </div>

              {/* PO / Order — dynamic per print job, same reasoning as lot code: a PO changes
                  per order, baking it into the label version would force re-approval every time.
                  Picking an order is optional and just auto-fills + links; free text still works. */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  PO Number <span className="text-muted fw-normal">(PLU on some customer specs)</span>
                </label>
                {loadingOrders ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />Loading open orders…
                  </div>
                ) : openOrders.length > 0 ? (
                  <select className="form-select form-select-sm mb-2" value={customerOrderId ?? ''} onChange={handleOrderSelect}>
                    <option value="">— Select an open order (optional) —</option>
                    {openOrders.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.poNumber ? `PO ${o.poNumber}` : o.orderNumber}
                        {o.customerName ? ` — ${o.customerName}` : ''}
                      </option>
                    ))}
                  </select>
                ) : null}
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={openOrders.length > 0 ? 'or type a PO number…' : 'Type a PO number (optional)…'}
                  value={poNumber}
                  onChange={handlePoNumberChange}
                />
                {customerOrderId != null && (
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    ✓ Linked to order — this print job will be traceable back to it.
                  </div>
                )}
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Production Date</label>
                  <input type="date" className="form-control form-control-sm"
                    value={productionDate} onChange={e => setProductionDate(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                    Expiration Date
                    {template?.Product?.shelfLifeDays && (
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
                        ({template.Product.shelfLifeDays}d shelf life)
                      </span>
                    )}
                  </label>
                  <input type="date" className="form-control form-control-sm"
                    value={expirationDate} onChange={e => setExpirationDate(e.target.value)} />
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Copies</label>
                  <input type="number" className="form-control form-control-sm" min={1} max={100}
                    value={copies} onChange={e => setCopies(Math.max(1, parseInt(e.target.value) || 1))} />
                </div>
                <div className="col-8">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Date Format</label>
                  <select className="form-select form-select-sm" value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="DD-MMM-YYYY">DD-MMM-YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="DD-MON-YY-CA">DD MON YY — CA (e.g. 10 SE 26)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Notes <span className="text-muted fw-normal">(optional)</span>
                </label>
                <textarea className="form-control form-control-sm" rows={2}
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={confirming}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={confirming}
                onClick={() => onConfirm({ lotCode, poNumber: poNumber.trim() || null, customerOrderId, productionDate, expirationDate, dateFormat, notes, copies, variantId: selectedVariantId || null })}>
                {confirming
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Preparing…</>
                  : '🖨 Print'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Nutrition Facts Form ─────────────────────────────────────────────────────

// NRow is a plain function (not a component) so calling it as nRow({...})
// avoids React treating it as a new component type on every render,
// which would unmount/remount inputs and lose focus on each keystroke.
function nRow({ label, field, unit, hasDv = true, hasEs = false, nf, set }) {
  return (
    <tr key={field + (unit || '') + (hasEs ? 'es' : '')}>
      <td className="py-1 pe-2" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</td>
      <td className="py-1 pe-1">
        {hasEs ? (
          <div className="d-flex gap-1">
            <input type="text" className="form-control form-control-sm" placeholder="EN"
              style={{ fontSize: 11 }} value={nf[field]?.en ?? ''} onChange={e => set(field, 'en', e.target.value)} />
            <input type="text" className="form-control form-control-sm" placeholder="ES"
              style={{ fontSize: 11 }} value={nf[field]?.es ?? ''} onChange={e => set(field, 'es', e.target.value)} />
          </div>
        ) : (
          <div className="input-group input-group-sm">
            <input type="number" className="form-control" style={{ fontSize: 11 }}
              value={nf[field]?.[unit] ?? nf[field] ?? ''} min={0} step="any"
              onChange={e => {
                const v = e.target.value;
                if (unit) set(field, unit, v);
                else set(field, null, v);
              }} />
            {unit && <span className="input-group-text" style={{ fontSize: 11 }}>{unit}</span>}
          </div>
        )}
      </td>
      {hasDv && !hasEs && (
        <td className="py-1" style={{ width: 80 }}>
          {nf[field]?.dv !== undefined && (
            <div className="input-group input-group-sm">
              <input type="number" className="form-control" style={{ fontSize: 11 }}
                value={nf[field]?.dv ?? ''} min={0} max={100}
                onChange={e => set(field, 'dv', e.target.value)} />
              <span className="input-group-text" style={{ fontSize: 11 }}>%</span>
            </div>
          )}
        </td>
      )}
      {(!hasDv || hasEs) && <td />}
    </tr>
  );
}

function NutritionForm({ nf, onChange }) {
  const set = (field, subfield, value) => {
    if (subfield) {
      onChange({ ...nf, [field]: { ...nf[field], [subfield]: value } });
    } else {
      onChange({ ...nf, [field]: value });
    }
  };

  const r = (props) => nRow({ ...props, nf, set });

  return (
    <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
      <thead>
        <tr className="table-light">
          <th style={{ fontSize: 11 }}>Nutrient</th>
          <th style={{ fontSize: 11 }}>Amount</th>
          <th style={{ fontSize: 11 }}>% DV</th>
        </tr>
      </thead>
      <tbody>
        {r({ label: 'Serving Size',           field: 'servingSize',          hasEs: true, hasDv: false })}
        {r({ label: 'Servings per container', field: 'servingsPerContainer', hasEs: true, hasDv: false })}
        <tr key="calories">
          <td className="py-1 pe-2" style={{ fontSize: 12 }}>Calories</td>
          <td className="py-1 pe-1" colSpan={2}>
            <input type="number" className="form-control form-control-sm" style={{ fontSize: 11 }}
              value={nf.calories ?? ''} min={0}
              onChange={e => set('calories', null, e.target.value)} />
          </td>
        </tr>
        {r({ label: 'Total Fat',          field: 'totalFat',          unit: 'g'   })}
        {r({ label: 'Saturated Fat',      field: 'saturatedFat',      unit: 'g'   })}
        {r({ label: 'Trans Fat',          field: 'transFat',          unit: 'g',   hasDv: false })}
        {r({ label: 'Cholesterol',        field: 'cholesterol',       unit: 'mg'  })}
        {r({ label: 'Sodium',             field: 'sodium',            unit: 'mg'  })}
        {r({ label: 'Total Carbohydrate', field: 'totalCarbohydrate', unit: 'g'   })}
        {r({ label: 'Dietary Fiber',      field: 'dietaryFiber',      unit: 'g'   })}
        {r({ label: 'Total Sugars',       field: 'totalSugars',       unit: 'g',   hasDv: false })}
        {r({ label: 'Added Sugars',       field: 'addedSugars',       unit: 'g'   })}
        {r({ label: 'Protein',            field: 'protein',           unit: 'g',   hasDv: false })}
        {r({ label: 'Vitamin D',          field: 'vitaminD',          unit: 'mcg' })}
        {r({ label: 'Calcium',            field: 'calcium',           unit: 'mg'  })}
        {r({ label: 'Iron',               field: 'iron',              unit: 'mg'  })}
        {r({ label: 'Potassium',          field: 'potassium',         unit: 'mg'  })}
      </tbody>
    </table>
  );
}

// ─── Content Editor ───────────────────────────────────────────────────────────

function ContentEditor({ template, version, onSaved, onReload, onLogoUrl }) {
  const { hasModule } = usePermissions();
  const [content, setContent] = useState(() => {
    const existing = version?.snapshot?.content || {};
    return { ...EMPTY_CONTENT, ...existing, nutritionFacts: { ...EMPTY_CONTENT.nutritionFacts, ...(existing.nutritionFacts || {}) } };
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [allTemplates, setAllTemplates] = useState([]);
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloningContent, setCloningContent] = useState(false);
  const [importingNutrition, setImportingNutrition] = useState(false);
  const [importNutritionMsg, setImportNutritionMsg] = useState('');
  const [caseUpcManual, setCaseUpcManual] = useState(false);

  // Re-init when version changes — intentionally depend only on version.id,
  // not on snapshot.content, to avoid resetting the form on every keystroke.
  useEffect(() => {
    const existing = version?.snapshot?.content || {};
    setContent({ ...EMPTY_CONTENT, ...existing, nutritionFacts: { ...EMPTY_CONTENT.nutritionFacts, ...(existing.nutritionFacts || {}) } });
    setSaveMsg('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.id]);

  // Load all templates for cloning
  useEffect(() => {
    productLabelService.getTemplates().then(setAllTemplates).catch(() => {});
  }, []);

  const handleCloneContent = async () => {
    if (!cloneSourceId) return;
    setCloningContent(true);
    try {
      const src = await productLabelService.getTemplate(cloneSourceId);
      const activeVersion = src.Versions?.find(v => v.id === src.activeVersionId) || src.Versions?.[0];
      const srcContent = activeVersion?.snapshot?.content;
      if (srcContent) {
        setContent(c => ({
          ...c,
          ...srcContent,
          nutritionFacts: { ...EMPTY_CONTENT.nutritionFacts, ...(srcContent.nutritionFacts || {}) },
        }));
      }
      setCloneSourceId('');
    } catch {
      // silent
    } finally {
      setCloningContent(false);
    }
  };

  // Daily Values for DV% calculation when importing nutrition
  const LABEL_DV = { totalFat: 78, saturatedFat: 20, cholesterol: 300, sodium: 2300,
    totalCarbs: 275, dietaryFiber: 28, addedSugars: 50, vitaminD: 20, calcium: 1300, iron: 18, potassium: 4700 };
  // Vitamins/minerals get FDA's tiered %DV rounding (21 CFR 101.9(c)(8)(iii)), applied by
  // LabelRenderer.js's fdaRoundMicroDV() at PRINT time — so the raw (unrounded) ratio must be
  // stored here, not a whole-percent round. Rounding it here first would double-round borderline
  // values into the wrong band (e.g. a true 2.5% DV would wrongly end up printed as 4% instead of
  // the correct 2% — exactly the bug this comment is guarding against). Every other nutrient uses
  // the simple whole-percent %DV rule, so it's safe (and already correct) to round here.
  const MICRO_DV_KEYS = ['vitaminD', 'calcium', 'iron', 'potassium'];
  const dvPct = (val, key) => {
    if (!LABEL_DV[key] || val == null) return '';
    const raw = (Number(val) / LABEL_DV[key]) * 100;
    return String(MICRO_DV_KEYS.includes(key) ? raw : Math.round(raw));
  };

  const handleImportNutrition = async () => {
    if (!template?.productId) {
      setImportNutritionMsg('This label template is not linked to a product.');
      return;
    }
    setImportingNutrition(true);
    setImportNutritionMsg('');
    try {
      const cert = await getNutritionCertificate(template.productId);
      const p = cert.profile;
      if (!p) throw new Error('No nutrition profile found for this product.');
      if (cert.isDraft) setImportNutritionMsg('⚠ Imported from draft (not yet approved). Approve the label in the product nutrition tab for an official version.');

      // Parse through Number to strip trailing zeros from DB decimal strings (e.g. "5.0000" → "5")
      const n = v => v != null ? String(parseFloat(v)) : '';

      const mapped = {
        bilingual: content.nutritionFacts?.bilingual ?? true,
        servingSize:          { en: p.servingSizeLabel || `${n(p.servingSize)}g`, es: content.nutritionFacts?.servingSize?.es ?? '' },
        servingsPerContainer: { en: n(p.servingsPerContainer), es: content.nutritionFacts?.servingsPerContainer?.es ?? '' },
        calories:             n(p.calories),
        totalFat:             { g: n(p.totalFat),           dv: dvPct(p.totalFat,       'totalFat') },
        saturatedFat:         { g: n(p.saturatedFat),       dv: dvPct(p.saturatedFat,   'saturatedFat') },
        transFat:             { g: n(p.transFat) },
        cholesterol:          { mg: n(p.cholesterol),       dv: dvPct(p.cholesterol,    'cholesterol') },
        sodium:               { mg: n(p.sodium),            dv: dvPct(p.sodium,         'sodium') },
        totalCarbohydrate:    { g: n(p.totalCarbs),         dv: dvPct(p.totalCarbs,     'totalCarbs') },
        dietaryFiber:         { g: n(p.dietaryFiber),       dv: dvPct(p.dietaryFiber,   'dietaryFiber') },
        totalSugars:          { g: n(p.totalSugars) },
        addedSugars:          { g: n(p.addedSugars),        dv: dvPct(p.addedSugars,    'addedSugars') },
        protein:              { g: n(p.protein) },
        vitaminD:             { mcg: n(p.vitaminD),         dv: dvPct(p.vitaminD,       'vitaminD') },
        calcium:              { mg: n(p.calcium),           dv: dvPct(p.calcium,        'calcium') },
        iron:                 { mg: n(p.iron),              dv: dvPct(p.iron,           'iron') },
        potassium:            { mg: n(p.potassium),         dv: dvPct(p.potassium,      'potassium') },
      };
      setContent(c => ({ ...c, nutritionFacts: mapped, _nutritionProductId: template.productId }));
      if (!cert.isDraft) setImportNutritionMsg(`✓ Imported from approved v${cert.version?.versionNumber} (${new Date(cert.version?.approvedAt).toLocaleDateString()})`);
    } catch (e) {
      setImportNutritionMsg(`Error: ${e.response?.data?.error || e.message}`);
    }
    setImportingNutrition(false);
  };

  // Extract blocks of a given type from the version snapshot (canvas or legacy)
  const extractBlocks = (type) => {
    const snapshot = version?.snapshot;
    if (!snapshot) return [];
    if (Array.isArray(snapshot.canvas)) return snapshot.canvas.filter(b => b.type === type);
    const acc = [];
    const collect = (list) => (list || []).forEach(b => { if (b.type === type) acc.push(b); });
    if (snapshot.columns) snapshot.columns.forEach(p => { if (p.rows) p.rows.forEach(r => (r.columns || []).forEach(c => collect(c.blocks))); else collect(p.blocks); });
    else if (snapshot.rows) snapshot.rows.forEach(r => (r.columns || []).forEach(c => collect(c.blocks)));
    return acc;
  };
  const customTextBlocks  = extractBlocks('custom_text');
  const customImageBlocks = extractBlocks('custom_image');

  // Asset library for custom_image picker
  const [assets, setAssets]           = useState([]);
  const [assetUploading, setAssetUploading] = useState(false);
  const [assetUploadErr, setAssetUploadErr] = useState('');
  const assetFileRef = useRef(null);
  const [uploadingForBlockId, setUploadingForBlockId] = useState(null);

  useEffect(() => {
    if (customImageBlocks.length > 0) {
      productLabelService.getAssets().then(setAssets).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.id]);

  const handleAssetUpload = async (e, blockId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ALLOWED = ['image/jpeg','image/jpg','image/png','image/webp','image/gif','image/svg+xml'];
    if (!ALLOWED.includes(file.type)) { setAssetUploadErr('Only JPEG, PNG, WebP, GIF, SVG.'); return; }
    if (file.size > 5 * 1024 * 1024) { setAssetUploadErr('Max 5 MB.'); return; }
    setAssetUploadErr(''); setAssetUploading(true); setUploadingForBlockId(blockId);
    try {
      const { uploadUrl, publicUrl, key } = await productLabelService.getAssetUploadUrl(file.name, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const asset = await productLabelService.createAsset({ name, fileUrl: publicUrl, s3Key: key, fileSize: file.size, mimeType: file.type });
      setAssets(prev => [asset, ...prev]);
      set('customImages', { ...content.customImages, [blockId]: asset.fileUrl });
    } catch { setAssetUploadErr('Upload failed.'); }
    finally { setAssetUploading(false); setUploadingForBlockId(null); e.target.value = ''; }
  };

  // Auto-fill product name, net weight, and manufacturer info on first load if empty
  useEffect(() => {
    const prod = template?.Product;
    if (!prod) return;

    const applyProductFields = (mfgInfo) => {
      setContent(c => {
        const updates = {};
        if (!c.productName && prod.name) updates.productName = prod.name;
        if (!c.netWeight && prod.unitNetWeightValue) {
          const val = parseFloat(prod.unitNetWeightValue);
          const unit = prod.unitNetWeightUnit || 'lb';
          updates.netWeight = `${val} ${unit}`;
        }
        // Case labels always resolve to the case/ITF-14 UPC — never auto-fill the product UPC,
        // otherwise it silently wins over the case UPC in the barcode block fallback (see LabelRenderer).
        if (template?.labelType === 'case') {
          if (c.upc) updates.upc = ''; // clear any stale value from before this field was hidden
        } else {
          if (!c.upc && prod.gtin) updates.upc = prod.gtin;
          if (c.caseUpc) updates.caseUpc = ''; // clear any stale value from before this field was hidden
        }
        if (!c.manufacturerInfo && mfgInfo) updates.manufacturerInfo = mfgInfo;
        return Object.keys(updates).length ? { ...c, ...updates } : c;
      });
    };

    companyService.getCompanies()
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        if (company?.logoUrl) onLogoUrl?.(company.logoUrl);
        const parts = [
          company?.name,
          [company?.addressLine1, company?.addressLine2].filter(Boolean).join(', '),
          [company?.city, company?.stateProvince, company?.postalCode].filter(Boolean).join(', '),
          company?.phone,
        ].filter(Boolean);
        applyProductFields(parts.join('\n'));
      })
      .catch(() => applyProductFields(''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.Product?.name]);

  const set = (field, value) => setContent(c => ({ ...c, [field]: value }));

  // Case weight drives both the display text and the GS1-128 barcode — kept in sync here so they
  // can never diverge the way netWeight/caseNetWeightLb did before this existed (see
  // docs/PRODUCT_LABELS_MODULE.md). Always overwrites netWeight rather than only-if-empty,
  // since case-label weight text is formulaic and staying byte-accurate to the barcode matters
  // more here than preserving a hand-tweaked display string.
  const setCaseWeight = (value) => {
    const num = parseFloat(value);
    setContent(c => ({
      ...c,
      caseNetWeightLb: value,
      netWeight: value !== '' && !Number.isNaN(num) ? `NET WT.: ${num.toFixed(2)} LBS` : c.netWeight,
    }));
  };

  const handleGenerateIngredients = async () => {
    if (!template?.productId) return;
    setGenerating(true);
    try {
      const ALLERGEN_DISPLAY = { milk: 'Milk', eggs: 'Eggs', fish: 'Fish', shellfish: 'Shellfish', tree_nuts: 'Tree Nuts', wheat: 'Wheat', peanuts: 'Peanuts', soybeans: 'Soybeans', sesame: 'Sesame' };
      const { ingredientsText, allergens } = await productLabelService.generateIngredients(template.productId);
      setContent(c => ({ ...c, ingredients: ingredientsText, allergens: allergens.map(a => ALLERGEN_DISPLAY[a] || a).join(', '), _ingredientsProductId: template.productId }));
    } catch {
      // silent
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const currentSnapshot = version.snapshot || {};
      const { content: _old, ...design } = currentSnapshot;
      const newSnapshot = { ...design, content };
      await productLabelService.updateVersion(template.id, version.id, { snapshot: newSnapshot });
      setSaveMsg('Saved');
      onSaved();
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = version?.status !== 'draft' && version?.status !== 'rejected';

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span className="fw-semibold" style={{ fontSize: 14 }}>
          Content Editor
          {version && (
            <span className="ms-2"><StatusBadge status={version.status} /></span>
          )}
        </span>
        <div className="d-flex align-items-center gap-2">
          {saveMsg && (
            <span className={`small ${saveMsg === 'Saved' ? 'text-success' : 'text-danger'}`}>
              {saveMsg}
            </span>
          )}
          {!isReadOnly && (
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving…</> : 'Save Content'}
            </button>
          )}
        </div>
      </div>

      {!isReadOnly && allTemplates.filter(t => t.id !== template?.id && t.activeVersionId).length > 0 && (
        <div className="d-flex gap-2 align-items-center mb-3 p-2 bg-light border rounded" style={{ fontSize: 13 }}>
          <span className="text-muted" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>Clone content from:</span>
          <select className="form-select form-select-sm flex-grow-1"
            value={cloneSourceId}
            onChange={e => setCloneSourceId(e.target.value)}>
            <option value="">Select a label…</option>
            {allTemplates
              .filter(t => t.id !== template?.id && t.activeVersionId)
              .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-outline-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}
            onClick={handleCloneContent} disabled={!cloneSourceId || cloningContent}>
            {cloningContent
              ? <span className="spinner-border spinner-border-sm" role="status" />
              : 'Import'}
          </button>
        </div>
      )}

      {isReadOnly && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
          This version is <strong>{STATUS_BADGE[version.status]?.label || version.status}</strong> and cannot be edited.
        </div>
      )}

      {(() => {
        // Stale-content warnings: shown when content exists but was not generated/imported for this product.
        // Stamps (_ingredientsProductId, _nutritionProductId) are set on generate/import/manual edit.
        // Missing or mismatched stamp means the content likely came from cloning another label.
        const pid = template?.productId;
        const ingredientsStale = !isReadOnly && !!content.ingredients && content._ingredientsProductId !== pid;
        const nutritionStale = !isReadOnly && !!content.nutritionFacts?.calories?.en && content._nutritionProductId !== pid;
        if (!ingredientsStale && !nutritionStale) return null;
        return (
          <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: 12 }}>
            <strong>⚠ Review required before saving</strong>
            <ul className="mb-0 mt-1">
              {ingredientsStale && <li>Ingredients may be from another product — regenerate from recipe or edit manually to confirm.</li>}
              {nutritionStale && <li>Nutrition facts may be from another product — re-import from the approved nutrition label to confirm.</li>}
            </ul>
          </div>
        );
      })()}

      <div className="accordion" id="contentAccordion">

        {/* ── Basic Info ── */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button py-2" type="button" data-bs-toggle="collapse"
              data-bs-target="#sec-basic" aria-expanded="true" style={{ fontSize: 13 }}>
              Basic Info
            </button>
          </h2>
          <div id="sec-basic" className="accordion-collapse collapse show" data-bs-parent="#contentAccordion">
            <div className="accordion-body py-2 px-3">
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Product Name</label>
                <input type="text" className="form-control form-control-sm" value={content.productName}
                  onChange={e => set('productName', e.target.value)} disabled={isReadOnly} />
              </div>
              {template?.labelType === 'case' && (
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>
                    Case Net Weight (lb) <span className="text-muted fw-normal">(drives the GS1-128 barcode + Net Weight text below)</span>
                  </label>
                  <input type="number" step="0.01" min="0" className="form-control form-control-sm" style={{ maxWidth: 160 }}
                    placeholder="e.g. 28.00" value={content.caseNetWeightLb || ''}
                    onChange={e => setCaseWeight(e.target.value)} disabled={isReadOnly} />
                </div>
              )}
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Net Weight</label>
                <input type="text" className="form-control form-control-sm" placeholder="e.g. 16 oz (454g)"
                  value={content.netWeight} onChange={e => set('netWeight', e.target.value)} disabled={isReadOnly} />
                {template?.labelType === 'case' && content.caseNetWeightLb && (
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    Auto-filled from Case Net Weight above — edit freely, it just won't re-sync unless you change the number above.
                  </div>
                )}
              </div>
              {template?.labelType !== 'case' && (
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>Product UPC <span className="text-muted fw-normal">(12-digit UPC-A)</span></label>
                  <input type="text" className="form-control form-control-sm" placeholder="e.g. 012345678905"
                    value={content.upc} onChange={e => set('upc', e.target.value)} disabled={isReadOnly} />
                  {(() => {
                    const digits = (content.upc || '').replace(/\D/g, '');
                    if (digits.length >= 11) {
                      let sum = 0;
                      for (let i = 0; i < 11; i++) sum += parseInt(digits[i], 10) * (i % 2 === 0 ? 3 : 1);
                      const expected = (10 - (sum % 10)) % 10;
                      const entered = digits.length === 12 ? parseInt(digits[11], 10) : null;
                      if (entered !== null && entered !== expected) {
                        return <div className="text-danger mt-1" style={{ fontSize: 11 }}>Check digit mismatch — expected <strong>{expected}</strong>, got <strong>{entered}</strong>. Barcode will be auto-corrected when printed.</div>;
                      }
                      return <div className="text-muted mt-1" style={{ fontSize: 11 }}>Check digit: <strong>{expected}</strong> ✓</div>;
                    }
                    return null;
                  })()}
                </div>
              )}
              {template?.labelType === 'case' && (
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>Case UPC <span className="text-muted fw-normal">(14-digit ITF-14)</span></label>
                  {(() => {
                    const presentations = (template?.Product?.Presentations || []).filter(p => p.gtin);
                    if (presentations.length > 0 && !caseUpcManual) {
                      return (
                        <div className="d-flex gap-1">
                          <select className="form-select form-select-sm"
                            value={content.caseUpc || ''}
                            onChange={e => set('caseUpc', e.target.value)}
                            disabled={isReadOnly}>
                            <option value="">— Select presentation —</option>
                            {presentations.map(p => (
                              <option key={p.id} value={p.gtin}>{p.name} — {p.gtin}</option>
                            ))}
                          </select>
                          <button className="btn btn-outline-secondary btn-sm flex-shrink-0"
                            onClick={() => setCaseUpcManual(true)} disabled={isReadOnly}
                            title="Enter manually">✎</button>
                        </div>
                      );
                    }
                    return (
                      <div className="d-flex gap-1">
                        <input type="text" className="form-control form-control-sm" placeholder="e.g. 10012345678905"
                          value={content.caseUpc || ''}
                          onChange={e => set('caseUpc', e.target.value)}
                          disabled={isReadOnly} />
                        {presentations.length > 0 && (
                          <button className="btn btn-outline-secondary btn-sm flex-shrink-0"
                            onClick={() => setCaseUpcManual(false)} disabled={isReadOnly}
                            title="Pick from presentations">↩</button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {template?.labelType === 'case' && (
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>
                    PLU / SKU <span className="text-muted fw-normal">(customer's own product code — not the PO number, which is entered per print job)</span>
                  </label>
                  <input type="text" className="form-control form-control-sm" style={{ maxWidth: 200 }}
                    placeholder="e.g. 8038" value={content.pluSku || ''}
                    onChange={e => set('pluSku', e.target.value)} disabled={isReadOnly} />
                </div>
              )}
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Expiration Label</label>
                <div className="d-flex gap-2 align-items-center">
                  <select
                    className="form-select form-select-sm"
                    value={template?.expirationLabel || 'best_by'}
                    disabled={!hasModule('labels.edit')}
                    onChange={async e => {
                      try { await productLabelService.updateTemplate(template.id, { expirationLabel: e.target.value }); onReload(); }
                      catch { /* silent */ }
                    }}
                  >
                    <option value="best_by">Best By</option>
                    <option value="use_by">Use By</option>
                    <option value="exp">Exp.</option>
                    <option value="best_if_used_by">Best If Used By</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                {template?.expirationLabel === 'custom' && (
                  <input type="text" className="form-control form-control-sm mt-2" style={{ maxWidth: 260 }}
                    placeholder="Custom label text, e.g. Freeze By"
                    defaultValue={template?.expirationLabelEn || ''}
                    disabled={!hasModule('labels.edit')}
                    onBlur={async e => {
                      const val = e.target.value.trim();
                      if (val === (template?.expirationLabelEn || '')) return;
                      try { await productLabelService.updateTemplate(template.id, { expirationLabelEn: val }); onReload(); }
                      catch { /* silent */ }
                    }}
                  />
                )}
              </div>
              <div className="mb-0">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Default Date Format</label>
                <div className="d-flex gap-2 align-items-center">
                  <select
                    className="form-select form-select-sm"
                    value={template?.dateFormat || 'MM/DD/YYYY'}
                    disabled={!hasModule('labels.edit')}
                    onChange={async e => {
                      try { await productLabelService.updateTemplate(template.id, { dateFormat: e.target.value }); onReload(); }
                      catch { /* silent */ }
                    }}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 12/31/2025)</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 31/12/2025)</option>
                    <option value="DD-MMM-YYYY">DD-MMM-YYYY (e.g. 31-Dec-2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2025-12-31)</option>
                    <option value="DD-MON-YY-CA">DD MON YY — Canada bilingual (e.g. 10 SE 26)</option>
                  </select>
                </div>
                <div className="form-text" style={{ fontSize: 11 }}>Can be overridden per print job.</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Storage & Cooking ── */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed py-2" type="button" data-bs-toggle="collapse"
              data-bs-target="#sec-storage" style={{ fontSize: 13 }}>
              Storage & Cooking Instructions
            </button>
          </h2>
          <div id="sec-storage" className="accordion-collapse collapse" data-bs-parent="#contentAccordion">
            <div className="accordion-body py-2 px-3">
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Storage Instructions</label>
                <textarea className="form-control form-control-sm" rows={2} value={content.storageInstructions}
                  onChange={e => set('storageInstructions', e.target.value)} disabled={isReadOnly} />
              </div>
              <div className="mb-0">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Cooking Instructions</label>
                <textarea className="form-control form-control-sm" rows={4} value={content.cookingInstructions}
                  onChange={e => set('cookingInstructions', e.target.value)} disabled={isReadOnly}
                  placeholder="e.g. CONVENTIONAL OVEN: Preheat oven to 425°F (220°C)..." />
              </div>
            </div>
          </div>
        </div>

        {/* ── Ingredients & Allergens ── */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed py-2" type="button" data-bs-toggle="collapse"
              data-bs-target="#sec-ingredients" style={{ fontSize: 13 }}>
              Ingredients & Allergens
              {!isReadOnly && !!content.ingredients && content._ingredientsProductId !== template?.productId && (
                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>⚠ review</span>
              )}
            </button>
          </h2>
          <div id="sec-ingredients" className="accordion-collapse collapse" data-bs-parent="#contentAccordion">
            <div className="accordion-body py-2 px-3">
              {!isReadOnly && !!content.ingredients && content._ingredientsProductId !== template?.productId && (
                <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: 11 }}>
                  ⚠ These ingredients may be from another product. Click "Generate from Recipe" to update, or edit manually to confirm.
                </div>
              )}
              {!isReadOnly && (
                <button className="btn btn-outline-success btn-sm mb-2" onClick={handleGenerateIngredients}
                  disabled={generating}>
                  {generating
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Generating…</>
                    : '⚗ Generate from Recipe'}
                </button>
              )}
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Ingredients</label>
                <textarea className="form-control form-control-sm" rows={5} value={content.ingredients}
                  onChange={e => setContent(c => ({ ...c, ingredients: e.target.value, _ingredientsProductId: template.productId }))} disabled={isReadOnly}
                  placeholder="Enriched Wheat Flour (Flour, Niacin...), Water, Mozzarella Cheese (Pasteurized Milk, ...)" />
              </div>
              <div className="mb-0">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Allergen Statement (CONTAINS:)</label>
                <input type="text" className="form-control form-control-sm" placeholder="e.g. Wheat, Milk"
                  value={content.allergens} onChange={e => set('allergens', e.target.value)} disabled={isReadOnly} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Manufacturer Info ── */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed py-2" type="button" data-bs-toggle="collapse"
              data-bs-target="#sec-mfg" style={{ fontSize: 13 }}>
              Manufacturer & Origin
            </button>
          </h2>
          <div id="sec-mfg" className="accordion-collapse collapse" data-bs-parent="#contentAccordion">
            <div className="accordion-body py-2 px-3">
              <div className="mb-2">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Manufacturer Info</label>
                <textarea className="form-control form-control-sm" rows={3} value={content.manufacturerInfo}
                  onChange={e => set('manufacturerInfo', e.target.value)} disabled={isReadOnly}
                  placeholder="Manufactured by Pizzacini Foods, LLC&#10;1 Artisan Way, Chicago, IL 60601" />
              </div>
              <div className="mb-0">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>Country of Origin</label>
                <input type="text" className="form-control form-control-sm" placeholder="e.g. PRODUCT OF USA"
                  value={content.countryOfOrigin} onChange={e => set('countryOfOrigin', e.target.value)} disabled={isReadOnly} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Nutrition Facts ── */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button collapsed py-2" type="button" data-bs-toggle="collapse"
              data-bs-target="#sec-nf" style={{ fontSize: 13 }}>
              Nutrition Facts (EN / ES)
              {!isReadOnly && !!content.nutritionFacts?.calories?.en && content._nutritionProductId !== template?.productId && (
                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>⚠ review</span>
              )}
            </button>
          </h2>
          <div id="sec-nf" className="accordion-collapse collapse" data-bs-parent="#contentAccordion">
            <div className="accordion-body py-2 px-2">
              {!isReadOnly && !!content.nutritionFacts?.calories?.en && content._nutritionProductId !== template?.productId && (
                <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: 11 }}>
                  ⚠ These nutrition facts may be from another product. Click "Import from Approved Nutrition Label" to update.
                </div>
              )}
              {/* Import + bilingual controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                {template?.productId && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleImportNutrition}
                    disabled={importingNutrition || isReadOnly}>
                    {importingNutrition ? 'Importing…' : '⬇ Import from Approved Nutrition Label'}
                  </button>
                )}
                <div className="form-check form-switch mb-0 ms-auto">
                  <input className="form-check-input" type="checkbox" id="nfBilingual"
                    checked={content.nutritionFacts?.bilingual !== false}
                    disabled={isReadOnly}
                    onChange={e => set('nutritionFacts', { ...content.nutritionFacts, bilingual: e.target.checked })} />
                  <label className="form-check-label" htmlFor="nfBilingual" style={{ fontSize: 12 }}>
                    Bilingual (EN / ES)
                  </label>
                </div>
              </div>
              {importNutritionMsg && (
                <div className={`alert py-1 px-2 mb-2 small ${importNutritionMsg.startsWith('Error') ? 'alert-danger' : importNutritionMsg.startsWith('⚠') ? 'alert-warning' : 'alert-success'}`}>
                  {importNutritionMsg}
                </div>
              )}
              <NutritionForm
                nf={content.nutritionFacts}
                onChange={nf => set('nutritionFacts', nf)}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </div>

        {/* ── Custom Text blocks ── */}
        {customTextBlocks.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button py-2" type="button" data-bs-toggle="collapse"
                data-bs-target="#sec-custom-text" style={{ fontSize: 13 }}>
                Custom Text
              </button>
            </h2>
            <div id="sec-custom-text" className="accordion-collapse collapse show" data-bs-parent="#contentAccordion">
              <div className="accordion-body py-2 px-3">
                {customTextBlocks.map((block, idx) => (
                  <div key={block.id} className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      {customTextBlocks.length > 1 ? `Custom Text ${idx + 1}` : 'Custom Text'}
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={content.customTexts?.[block.id] ?? block.customText ?? ''}
                      onChange={e => set('customTexts', { ...content.customTexts, [block.id]: e.target.value })}
                      disabled={isReadOnly}
                      placeholder={block.customText || 'Enter custom text…'}
                    />
                    {block.customText && (
                      <div className="form-text" style={{ fontSize: 11 }}>
                        Design default: "{block.customText}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Custom Image blocks ── */}
        {customImageBlocks.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button py-2" type="button" data-bs-toggle="collapse"
                data-bs-target="#sec-custom-image" style={{ fontSize: 13 }}>
                Custom Images
              </button>
            </h2>
            <div id="sec-custom-image" className="accordion-collapse collapse show" data-bs-parent="#contentAccordion">
              <div className="accordion-body py-2 px-3">
                {assetUploadErr && <div className="alert alert-danger py-1 mb-2" style={{ fontSize: 12 }}>{assetUploadErr}</div>}
                {customImageBlocks.map((block, idx) => {
                  const currentUrl = content.customImages?.[block.id] || block.imageUrl || '';
                  const isUploading = assetUploading && uploadingForBlockId === block.id;
                  const inputId = `asset-upload-${block.id}`;
                  return (
                    <div key={block.id} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label mb-0" style={{ fontSize: 12 }}>
                          {customImageBlocks.length > 1 ? `Custom Image ${idx + 1}` : 'Custom Image'}
                          {block.imageName ? <span className="text-muted ms-1">({block.imageName})</span> : null}
                        </label>
                        {!isReadOnly && (
                          <>
                            <label htmlFor={inputId} className="btn btn-outline-secondary btn-sm py-0 px-2 mb-0" style={{ fontSize: 11, cursor: 'pointer' }}>
                              {isUploading ? 'Uploading…' : '+ Upload new'}
                            </label>
                            <input id={inputId} ref={assetFileRef} type="file" accept="image/*" className="d-none"
                              onChange={e => handleAssetUpload(e, block.id)} disabled={isUploading} />
                          </>
                        )}
                      </div>
                      {/* Current selection preview */}
                      {currentUrl && (
                        <div className="mb-2 d-flex align-items-center gap-2">
                          <img src={currentUrl} alt="" style={{ height: 48, maxWidth: 120, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, padding: 2 }} />
                          <span className="text-muted" style={{ fontSize: 11 }}>Current image</span>
                          {!isReadOnly && (
                            <button className="btn btn-link btn-sm p-0 text-danger ms-auto" style={{ fontSize: 11 }}
                              onClick={() => set('customImages', { ...content.customImages, [block.id]: '' })}>
                              Remove
                            </button>
                          )}
                        </div>
                      )}
                      {/* Asset library thumbnails */}
                      {!isReadOnly && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto' }}>
                          {assets.length === 0 && (
                            <span className="text-muted" style={{ fontSize: 11 }}>No images in library yet — upload one above.</span>
                          )}
                          {assets.map(asset => {
                            const isSel = currentUrl === asset.fileUrl;
                            return (
                              <div key={asset.id} onClick={() => set('customImages', { ...content.customImages, [block.id]: asset.fileUrl })}
                                title={asset.name}
                                style={{ width: 56, height: 56, border: isSel ? '2px solid #be185d' : '1px solid #dee2e6', borderRadius: 4,
                                  cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: isSel ? '#fdf2f8' : '#fafafa', flexShrink: 0 }}>
                                <img src={asset.fileUrl} alt={asset.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Variant Modal (create / edit) ───────────────────────────────────────────

function VariantModal({ show, onClose, templateId, variant, onSaved }) {
  const [name, setName]                       = useState('');
  const [labelTitle, setLabelTitle]           = useState('');
  const [netWeight, setNetWeight]             = useState('');
  const [caseNetWeightLb, setCaseNetWeightLb] = useState('');
  const [pluSku, setPluSku]                   = useState('');
  const [upc, setUpc]                         = useState('');
  const [brandNameOverride, setBrandName]     = useState('');
  const [logoOverride, setLogoOverride]       = useState('');
  const [packDescription, setPackDescription] = useState('');
  const [isDefault, setIsDefault]             = useState(false);
  const [prodDateDisplay, setProdDateDisplay] = useState('inherit');
  const [prodDateLabel, setProdDateLabel]     = useState('');
  const [expLabelOverride, setExpLabelOverride] = useState('');
  const [expBlockTitleMode, setExpBlockTitleMode] = useState('default'); // 'default' | 'custom' | 'none'
  const [expBlockTitleText, setExpBlockTitleText] = useState('');
  const [manufacturerInfoOverride, setManufacturerInfoOverride] = useState('');
  const [customTextOverride, setCustomTextOverride] = useState('');
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');

  // Asset picker state
  const [assets, setAssets]                   = useState([]);
  const [assetLoading, setAssetLoading]       = useState(false);
  const [assetUploading, setAssetUploading]   = useState(false);
  const [assetUploadErr, setAssetUploadErr]   = useState('');
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const logoFileRef                           = useRef(null);

  // Populate fields when editing
  useEffect(() => {
    if (show) {
      setName(variant?.name || '');
      setLabelTitle(variant?.labelTitle || '');
      setNetWeight(variant?.netWeight || '');
      setCaseNetWeightLb(variant?.caseNetWeightLb != null ? String(variant.caseNetWeightLb) : '');
      setPluSku(variant?.pluSku || '');
      setUpc(variant?.upc || '');
      setBrandName(variant?.brandNameOverride || '');
      setLogoOverride(variant?.logoOverride || '');
      setPackDescription(variant?.packDescription || '');
      setIsDefault(variant?.isDefault || false);
      setProdDateDisplay(variant?.prodDateDisplay || 'inherit');
      setProdDateLabel(variant?.prodDateLabel || '');
      setExpLabelOverride(variant?.expLabelOverride || '');
      // Restore expBlockTitle mode from saved value
      if (variant?.expBlockTitle == null) { setExpBlockTitleMode('default'); setExpBlockTitleText(''); }
      else if (variant.expBlockTitle === '') { setExpBlockTitleMode('none'); setExpBlockTitleText(''); }
      else { setExpBlockTitleMode('custom'); setExpBlockTitleText(variant.expBlockTitle); }
      setManufacturerInfoOverride(variant?.manufacturerInfoOverride || '');
      setCustomTextOverride(variant?.customTextOverride || '');
      setShowAssetPicker(false);
      setAssetUploadErr('');
      setError('');
    }
  }, [show, variant]);

  // Load assets when picker is opened for the first time
  useEffect(() => {
    if (showAssetPicker && assets.length === 0 && !assetLoading) {
      setAssetLoading(true);
      productLabelService.getAssets()
        .then(setAssets)
        .catch(() => {})
        .finally(() => setAssetLoading(false));
    }
  }, [showAssetPicker, assets.length, assetLoading]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!ALLOWED.includes(file.type)) { setAssetUploadErr('Only JPEG, PNG, WebP, GIF, SVG allowed.'); return; }
    if (file.size > 5 * 1024 * 1024) { setAssetUploadErr('Max 5 MB.'); return; }
    setAssetUploadErr('');
    setAssetUploading(true);
    try {
      const { uploadUrl, publicUrl, key } = await productLabelService.getAssetUploadUrl(file.name, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      const name = file.name.replace(/\.[^.]+$/, '');
      const asset = await productLabelService.createAsset({ name, fileUrl: publicUrl, s3Key: key, fileSize: file.size, mimeType: file.type });
      setAssets(prev => [asset, ...prev]);
      setLogoOverride(asset.fileUrl);
      setShowAssetPicker(false);
    } catch { setAssetUploadErr('Upload failed.'); }
    finally { setAssetUploading(false); e.target.value = ''; }
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const data = {
        name: name.trim(),
        labelTitle: labelTitle.trim() || null,
        netWeight: netWeight.trim() || null,
        caseNetWeightLb: caseNetWeightLb.trim() !== '' ? Number(caseNetWeightLb) : null,
        pluSku: pluSku.trim() || null,
        upc: upc.trim() || null,
        brandNameOverride: brandNameOverride.trim() || null,
        logoOverride: logoOverride.trim() || null,
        packDescription: packDescription.trim() || null,
        isDefault,
        prodDateDisplay,
        prodDateLabel: prodDateLabel.trim() || null,
        expLabelOverride: expLabelOverride.trim() || null,
        expBlockTitle: expBlockTitleMode === 'none' ? '' : expBlockTitleMode === 'custom' ? (expBlockTitleText.trim() || null) : null,
        manufacturerInfoOverride: manufacturerInfoOverride.trim() || null,
        customTextOverride: customTextOverride.trim() || null,
      };
      let saved;
      if (variant) {
        saved = await productLabelService.updateVariant(templateId, variant.id, data);
      } else {
        saved = await productLabelService.createVariant(templateId, data);
      }
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save presentation.');
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{variant ? 'Edit Presentation' : 'New Presentation'}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}

              {/* Name */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Presentation Name <span className="text-danger">*</span>
                </label>
                <input className="form-control form-control-sm" value={name}
                  onChange={e => setName(e.target.value)} placeholder="e.g. Club 2-pack, Food Service 30ct, Retail 16oz" autoFocus />
              </div>

              {/* Label Title */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Label Title <span className="text-muted fw-normal">(overrides product name on label)</span>
                </label>
                <input className="form-control form-control-sm" value={labelTitle}
                  onChange={e => setLabelTitle(e.target.value)} placeholder="e.g. PIZZA MARGHERITA - 2 PACK - 10ct" />
              </div>

              {/* Net Weight + UPC */}
              <div className="row g-2 mb-3">
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Net Weight</label>
                  <input className="form-control form-control-sm" value={netWeight}
                    onChange={e => setNetWeight(e.target.value)} placeholder="e.g. 2 × 16oz (907g)" />
                </div>
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>UPC</label>
                  <input className="form-control form-control-sm" value={upc}
                    onChange={e => setUpc(e.target.value)} placeholder="e.g. 012345678901" />
                </div>
              </div>

              {/* Case Net Weight (lb) — GS1-128 barcode AI(3202), a real number, distinct from
                  the free-text Net Weight display field above */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Case Net Weight (lb) <span className="text-muted fw-normal">(GS1-128 barcode)</span>
                </label>
                <input type="number" step="0.01" min="0" className="form-control form-control-sm" style={{ maxWidth: 160 }}
                  value={caseNetWeightLb} onChange={e => setCaseNetWeightLb(e.target.value)} placeholder="e.g. 28.00" />
              </div>

              {/* PLU / SKU override — leave blank to use the base label's PLU/SKU */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  PLU / SKU Override <span className="text-muted fw-normal">(leave blank to use the base label's)</span>
                </label>
                <input className="form-control form-control-sm" style={{ maxWidth: 200 }}
                  value={pluSku} onChange={e => setPluSku(e.target.value)} placeholder="e.g. 8038" />
              </div>

              {/* Brand Name Override */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Brand Name Override <span className="text-muted fw-normal">(private label)</span>
                </label>
                <input className="form-control form-control-sm" value={brandNameOverride}
                  onChange={e => setBrandName(e.target.value)} placeholder="Customer brand name" />
              </div>

              {/* Logo Override — asset picker */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Logo Override <span className="text-muted fw-normal">(private label)</span>
                </label>
                {logoOverride ? (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: 64, height: 40, border: '1px solid #dee2e6', borderRadius: 4,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', overflow: 'hidden', flexShrink: 0 }}>
                      <img src={logoOverride} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                    <button type="button" className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12 }}
                      onClick={() => setShowAssetPicker(p => !p)}>
                      {showAssetPicker ? 'Close picker' : 'Change'}
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" style={{ fontSize: 12 }}
                      onClick={() => { setLogoOverride(''); setShowAssetPicker(false); }}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-outline-secondary btn-sm mb-2" style={{ fontSize: 12 }}
                    onClick={() => setShowAssetPicker(p => !p)}>
                    🖼 Select / Upload Logo
                  </button>
                )}
                {showAssetPicker && (
                  <div className="border rounded p-2" style={{ background: '#f8f9fa' }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: 12 }}>Image Library</span>
                      <div className="d-flex gap-2 align-items-center">
                        {assetUploadErr && <span className="text-danger" style={{ fontSize: 11 }}>{assetUploadErr}</span>}
                        <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2" style={{ fontSize: 11 }}
                          onClick={() => logoFileRef.current?.click()} disabled={assetUploading}>
                          {assetUploading ? '…' : '+ Upload'}
                        </button>
                        <input ref={logoFileRef} type="file" accept="image/*" className="d-none" onChange={handleLogoUpload} />
                      </div>
                    </div>
                    {assetLoading ? (
                      <div className="text-muted" style={{ fontSize: 12 }}>Loading…</div>
                    ) : assets.length === 0 ? (
                      <div className="text-muted" style={{ fontSize: 12 }}>No images yet. Upload one above.</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {assets.map(asset => {
                          const isSel = logoOverride === asset.fileUrl;
                          return (
                            <div key={asset.id} title={asset.name}
                              onClick={() => { setLogoOverride(asset.fileUrl); setShowAssetPicker(false); }}
                              style={{ width: 60, height: 60, border: isSel ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                borderRadius: 4, cursor: 'pointer', overflow: 'hidden', background: isSel ? '#e8f0fe' : '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <img src={asset.fileUrl} alt={asset.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pack Description */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Pack Description</label>
                <input className="form-control form-control-sm" value={packDescription}
                  onChange={e => setPackDescription(e.target.value)} placeholder="e.g. Contains 2 individually wrapped pieces" />
              </div>

              {/* Date options */}
              <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Date Options</div>

                {/* Expiration label (date line prefix) */}
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>
                    Expiration label <span className="text-muted fw-normal">(date line prefix — blank = template default)</span>
                  </label>
                  <input className="form-control form-control-sm" value={expLabelOverride}
                    onChange={e => setExpLabelOverride(e.target.value)}
                    placeholder="e.g. Best By, Use By, Exp, E., B.B." />
                </div>

                {/* Block title (bold header above the dates) */}
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>Block title</label>
                  <select className="form-select form-select-sm mb-1" value={expBlockTitleMode}
                    onChange={e => setExpBlockTitleMode(e.target.value)}>
                    <option value="default">Same as expiration label (default)</option>
                    <option value="custom">Custom text</option>
                    <option value="none">No title</option>
                  </select>
                  {expBlockTitleMode === 'custom' && (
                    <input className="form-control form-control-sm" value={expBlockTitleText}
                      onChange={e => setExpBlockTitleText(e.target.value)}
                      placeholder="e.g. DATES:, DATE INFORMATION:, BEST BEFORE:" />
                  )}
                </div>

                {/* Production date */}
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>Production date</label>
                  <div className="d-flex gap-3">
                    {[
                      { value: 'inherit', label: 'Inherit from design' },
                      { value: 'show',    label: 'Always show' },
                      { value: 'hide',    label: 'Always hide' },
                    ].map(opt => (
                      <div className="form-check" key={opt.value}>
                        <input className="form-check-input" type="radio"
                          id={`pdDisplay_${opt.value}`} name="prodDateDisplay"
                          value={opt.value} checked={prodDateDisplay === opt.value}
                          onChange={() => setProdDateDisplay(opt.value)} />
                        <label className="form-check-label" htmlFor={`pdDisplay_${opt.value}`} style={{ fontSize: 12 }}>
                          {opt.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Production date label prefix — only relevant when not hiding */}
                {prodDateDisplay !== 'hide' && (
                  <div>
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>
                      Production date label <span className="text-muted fw-normal">(leave blank for design default)</span>
                    </label>
                    <input className="form-control form-control-sm" value={prodDateLabel}
                      onChange={e => setProdDateLabel(e.target.value)}
                      placeholder="e.g. P., Prod.:, Packed On:, Production Date:" />
                  </div>
                )}
              </div>

              {/* Manufacturer Info Override */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Manufacturer Info <span className="text-muted fw-normal">(overrides manufacturer block on label)</span>
                </label>
                <textarea className="form-control form-control-sm" rows={3} value={manufacturerInfoOverride}
                  onChange={e => setManufacturerInfoOverride(e.target.value)}
                  placeholder={"e.g. Manufactured by Acme Foods\n123 Main St, Chicago, IL 60601\n(312) 555-0100 · acme.com"} />
              </div>

              {/* Custom Text Override */}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Custom Text <span className="text-muted fw-normal">(overrides custom text block on label)</span>
                </label>
                <textarea className="form-control form-control-sm" rows={2} value={customTextOverride}
                  onChange={e => setCustomTextOverride(e.target.value)}
                  placeholder="e.g. Distributed by Acme Foods, LLC" />
              </div>

              {/* Default flag */}
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="variantDefault"
                  checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
                <label className="form-check-label" htmlFor="variantDefault" style={{ fontSize: 13 }}>
                  Set as default presentation
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (variant ? 'Save Changes' : 'Add Presentation')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function LabelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModule } = usePermissions();

  const fromTab = location.state?.fromTab || 'product';
  const backTo = `/labels?tab=${fromTab}`;
  const backLabel = fromTab === 'case' ? 'Case Labels' : fromTab === 'print_history' ? 'Print History' : 'Product Labels';
  // Arriving from the product-first "Quick Print" flow — see ProductLabelsPage.js —
  // opens the print modal immediately with the chosen order pre-selected.
  const autoOpenPrint = location.state?.autoOpenPrint || false;
  const prefillOrderId = location.state?.prefillOrderId || null;
  const autoOpenedRef = useRef(false);

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const [approvingId, setApprovingId]   = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  const [showNewVersionModal, setShowNewVersionModal] = useState(false);

  const [companyLogoUrl, setCompanyLogoUrl]   = useState('');
  const [deleting, setDeleting]               = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showPrintModal, setShowPrintModal]   = useState(false);
  const [editingName, setEditingName]         = useState(false);
  const [nameValue, setNameValue]             = useState('');
  const [savingName, setSavingName]           = useState(false);
  const [printConfirming, setPrintConfirming] = useState(false);
  const [printError, setPrintError]           = useState('');
  const [printJob, setPrintJob]               = useState(null); // triggers the print area render

  // Variant management
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [editingVariant, setEditingVariant]     = useState(null);

  // Which version is being shown in the editor / preview
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  // Right panel tab: 'preview' | 'layout'
  const [rightTab, setRightTab] = useState('preview');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    productLabelService.getTemplate(id)
      .then(t => {
        setTemplate(t);
        // Default to draft > pending > latest version
        const versions = (t.Versions || []).slice().sort((a, b) => b.versionNumber - a.versionNumber);
        const preferred = versions.find(v => v.status === 'draft')
          || versions.find(v => v.status === 'pending_approval')
          || versions[0];
        if (preferred) setSelectedVersionId(v => v || preferred.id);
      })
      .catch(() => setError('Failed to load label.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // One-shot: open the print modal automatically when arriving from Quick Print.
  useEffect(() => {
    if (autoOpenPrint && template && !loading && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setPrintError('');
      setShowPrintModal(true);
    }
  }, [autoOpenPrint, template, loading]);

  const versions = (template?.Versions || []).slice().sort((a, b) => b.versionNumber - a.versionNumber);
  const selectedVersion = versions.find(v => v.id === selectedVersionId) || versions[0] || null;

  // The snapshot used for preview follows the selected version
  const previewSnapshot = selectedVersion?.snapshot || null;

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleApprove = async (version) => {
    setApprovingId(version.id);
    setActionError('');
    try {
      await productLabelService.approveVersion(template.id, version.id);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to approve version.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectConfirm = async (notes) => {
    if (!rejectTarget) return;
    setRejectLoading(true);
    setActionError('');
    try {
      await productLabelService.rejectVersion(template.id, rejectTarget.id, notes);
      setShowRejectModal(false);
      setRejectTarget(null);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to reject version.');
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSubmit = async (version) => {
    setSubmittingId(version.id);
    setActionError('');
    try {
      await productLabelService.submitVersion(template.id, version.id);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to submit for approval.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleNewVersionCreated = (newVersion) => {
    load();
    setSelectedVersionId(newVersion.id);
  };

  const [deleteDraftTarget, setDeleteDraftTarget] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const handleDeleteDraftVersion = async () => {
    if (!deleteDraftTarget) return;
    setDeletingDraft(true);
    try {
      await productLabelService.deleteVersion(template.id, deleteDraftTarget.id);
      setDeleteDraftTarget(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete version.');
    } finally {
      setDeletingDraft(false);
    }
  };

  const handleChangeDesign = async (versionId, designId) => {
    if (!designId) return;
    const allDesigns = await productLabelService.getDesigns().catch(() => []);
    const design = allDesigns.find(d => String(d.id) === String(designId));
    if (!design) return;
    const version = versions.find(v => v.id === versionId);
    const prevContent = version?.snapshot?.content || {};
    const layout = design.rows || {};
    const newSnapshot = { ...layout, width: design.width, height: design.height, content: prevContent };
    try {
      await productLabelService.updateVersion(template.id, versionId, { snapshot: newSnapshot });
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to change design.');
    }
  };

  const handlePrintConfirm = async (data) => {
    setPrintConfirming(true);
    setPrintError('');
    try {
      await productLabelService.logPrintJob(template.id, {
        lotCode: data.lotCode || null,
        poNumber: data.poNumber || null,
        customerOrderId: data.customerOrderId || null,
        productionDate: data.productionDate || null,
        expirationDate: data.expirationDate || null,
        notes: data.notes || null,
        variantId: data.variantId || null,
      });
      setShowPrintModal(false);
      setPrintJob(data);
      // Give React time to mount the portal + render barcodes, then print
      setTimeout(() => {
        window.print();
        setPrintJob(null);
      }, 300);
    } catch (err) {
      setPrintError(err?.response?.data?.message || 'Failed to prepare print job.');
    } finally {
      setPrintConfirming(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await productLabelService.deleteTemplate(template.id);
      navigate(backTo);
    } catch (err) {
      setShowDeleteModal(false);
      setActionError(err?.response?.data?.message || 'Failed to delete label.');
      setDeleting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="container mt-5 text-center text-muted">
        <span className="spinner-border spinner-border-sm me-2" role="status" />Loading label…
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error || 'Label not found.'}</div>
        <Link to={backTo} className="btn btn-outline-secondary btn-sm">← Back to {backLabel}</Link>
      </div>
    );
  }

  const activeVersion  = versions.find(v => v.id === template.activeVersionId);
  const pendingVersion  = versions.find(v => v.status === 'pending_approval');
  const draftVersion    = versions.find(v => v.status === 'draft');
  const rejectedVersion = versions.find(v => v.status === 'rejected');

  return (
    <div className="container-fluid mt-4 pb-5" style={{ maxWidth: 1400 }}>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-2">
        <ol className="breadcrumb" style={{ fontSize: 13 }}>
          <li className="breadcrumb-item"><Link to={backTo}>{backLabel}</Link></li>
          <li className="breadcrumb-item active" aria-current="page">{template.name}</li>
        </ol>
      </nav>

      {/* Action error */}
      {actionError && (
        <div className="alert alert-danger alert-dismissible fade show py-2 mb-3" role="alert">
          {actionError}
          <button type="button" className="btn-close" onClick={() => setActionError('')} />
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
        <div>
          {/* Label name — editable when there's a draft or rejected version */}
          {editingName ? (
            <form
              className="d-flex align-items-center gap-2 mb-1"
              onSubmit={async e => {
                e.preventDefault();
                const trimmed = nameValue.trim();
                if (!trimmed || trimmed === template.name) { setEditingName(false); return; }
                setSavingName(true);
                try {
                  await productLabelService.updateTemplate(template.id, { name: trimmed });
                  load();
                } catch { /* silent — name reverts */ }
                finally { setSavingName(false); setEditingName(false); }
              }}
            >
              <input
                className="form-control form-control-sm fw-semibold"
                style={{ fontSize: 20, height: 36, maxWidth: 320 }}
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                disabled={savingName}
                autoFocus
              />
              <button className="btn btn-primary btn-sm" type="submit" disabled={savingName || !nameValue.trim()}>
                {savingName ? '…' : 'Save'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setEditingName(false)} disabled={savingName}>
                Cancel
              </button>
            </form>
          ) : (
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="mb-0">{template.name}</h4>
              {hasModule('labels.edit') && (
                <button
                  className="btn btn-link btn-sm p-0 text-muted"
                  style={{ fontSize: 13, lineHeight: 1 }}
                  title="Rename label"
                  onClick={() => { setNameValue(template.name); setEditingName(true); }}
                >✏️</button>
              )}
            </div>
          )}
          <div className="text-muted" style={{ fontSize: 14 }}>
            Product: <strong>{template.Product?.name || '—'}</strong>
          </div>
          <div className="mt-1 d-flex gap-2 flex-wrap">
            {selectedVersion
              ? isSuperseded(selectedVersion, template.activeVersionId)
                  ? <StatusBadge status="superseded" />
                  : <StatusBadge status={selectedVersion.status} />
              : <StatusBadge status={template.status || 'draft'} />}
            {selectedVersion?.id === template.activeVersionId && (
              <span className="badge bg-primary" style={{ fontSize: 11 }}>★ Active</span>
            )}
            {template.expirationLabel && (
              <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                {resolveExpLabel(template)}
              </span>
            )}
          </div>
          {template.Product?.regulatoryAgency === 'usda' && selectedVersion
            && (selectedVersion.status === 'draft' || selectedVersion.status === 'rejected' || selectedVersion.status === 'pending_approval')
            && !usdaMarkPresent(selectedVersion.snapshot) && (
            <div className="alert alert-danger py-1 px-2 mt-2 mb-0" style={{ fontSize: 12, maxWidth: 480 }}>
              ⚠ USDA/FSIS-regulated product — missing the official mark of inspection. This
              version cannot be {selectedVersion.status === 'pending_approval' ? 'approved' : 'submitted for approval'} until
              the USDA Inspection Mark block is added and assigned an image.
            </div>
          )}
        </div>

        <div className="d-flex gap-2 flex-wrap">
          {hasModule('labels.edit') && (draftVersion || rejectedVersion) && (() => {
            const v = draftVersion || rejectedVersion;
            const missingUsdaMark = template.Product?.regulatoryAgency === 'usda' && !usdaMarkPresent(v.snapshot);
            return (
              <button className="btn btn-outline-warning btn-sm"
                onClick={() => handleSubmit(v)} disabled={submittingId === v.id || missingUsdaMark}
                title={missingUsdaMark ? 'This product is regulated by USDA/FSIS — add the USDA Inspection Mark block before submitting.' : undefined}>
                {submittingId === v.id
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Submitting…</>
                  : 'Submit for Approval'}
              </button>
            );
          })()}
          {hasModule('labels.approve') && pendingVersion && (
            <>
              <button className="btn btn-success btn-sm"
                onClick={() => handleApprove(pendingVersion)} disabled={approvingId === pendingVersion.id}>
                {approvingId === pendingVersion.id
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Approving…</>
                  : 'Approve'}
              </button>
              <button className="btn btn-danger btn-sm"
                onClick={() => { setRejectTarget(pendingVersion); setShowRejectModal(true); }}>
                Reject
              </button>
            </>
          )}
          {hasModule('labels.print') && activeVersion && (
            selectedVersion?.id === activeVersion.id ? (
              <button className="btn btn-primary btn-sm"
                onClick={() => { setPrintError(''); setShowPrintModal(true); }}>
                🖨 Print
              </button>
            ) : (
              <span className="text-muted" style={{ fontSize: 12, alignSelf: 'center' }}>
                Switch to v{activeVersion.versionNumber} to print
              </span>
            )
          )}
          {hasModule('labels.edit') && (
            <button className="btn btn-outline-primary btn-sm"
              onClick={() => setShowNewVersionModal(true)}>
              + New Version
            </button>
          )}
          {hasModule('labels.edit') && !activeVersion && template.status !== 'archived' && (
            <button className="btn btn-outline-danger btn-sm"
              onClick={() => setShowDeleteModal(true)}>
              Delete Label
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="row g-4">

        {/* ── Left: Content Editor ── */}
        <div className="col-xl-4 col-lg-5">

          {/* Version selector */}
          {versions.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Editing Version</label>
              <select className="form-select form-select-sm" value={selectedVersionId || ''}
                onChange={e => { setSelectedVersionId(Number(e.target.value)); setRightTab('preview'); }}>
                {versions.map(v => {
                  const sup = isSuperseded(v, template.activeVersionId);
                  const label = sup ? 'Superseded' : (STATUS_BADGE[v.status]?.label || v.status);
                  const suffix = v.id === template.activeVersionId ? ' ★ Active' : '';
                  return (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber} — {label}{suffix}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {selectedVersion ? (
            <div className="border rounded p-3 bg-light" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              <ContentEditor
                key={selectedVersion.id}
                template={template}
                version={selectedVersion}
                onSaved={load}
                onReload={load}
                onLogoUrl={setCompanyLogoUrl}
              />
            </div>
          ) : (
            <div className="border rounded p-4 text-center text-muted" style={{ fontSize: 13 }}>
              <p>No versions yet.</p>
              {hasModule('labels.edit') && (
                <p>Use <strong>+ New Version</strong> to create the first version.</p>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Preview + Version History ── */}
        <div className="col-xl-8 col-lg-7">

          {/* Live preview / Inline layout editor */}
          {(() => {
            const canEditLayout = hasModule('labels.edit') && selectedVersion &&
              (selectedVersion.status === 'draft' || selectedVersion.status === 'rejected');
            return (
              <div className="card mb-3">
                <div className="card-header d-flex align-items-center justify-content-between py-2">
                  <div className="d-flex gap-1">
                    <button
                      className={`btn btn-sm ${rightTab === 'preview' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setRightTab('preview')}
                    >
                      Preview
                    </button>
                    {canEditLayout && (
                      <button
                        className={`btn btn-sm ${rightTab === 'layout' ? 'btn-warning' : 'btn-outline-warning'}`}
                        onClick={() => setRightTab('layout')}
                        title="Edit block positions directly on the label"
                      >
                        ✏️ Edit Layout
                      </button>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {canEditLayout && (
                      <ChangeDesignSelect
                        templateId={id}
                        versionId={selectedVersion.id}
                        onChanged={handleChangeDesign}
                      />
                    )}
                    {selectedVersion && (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        v{selectedVersion.versionNumber} · <StatusBadge status={selectedVersion.status} />
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-body p-2" style={{ backgroundColor: '#f8f9fa' }}>
                  {rightTab === 'layout' && canEditLayout ? (
                    <InlineCanvasEditor
                      key={selectedVersion.id}
                      snapshot={selectedVersion.snapshot}
                      templateId={id}
                      versionId={selectedVersion.id}
                      onSaved={load}
                    />
                  ) : (
                    <ScaledPreview snapshot={previewSnapshot} logoUrl={companyLogoUrl} template={template} />
                  )}
                </div>
              </div>
            );
          })()}

          {/* Version history */}
          <div className="card">
            <div className="card-header py-2">
              <span className="fw-semibold" style={{ fontSize: 14 }}>
                Version History
                <span className="badge bg-secondary ms-2">{versions.length}</span>
              </span>
            </div>
            <div className="card-body p-0">
              {versions.length === 0 ? (
                <div className="text-muted text-center py-4" style={{ fontSize: 13 }}>No versions yet.</div>
              ) : (
                <div className="accordion accordion-flush" id="versionsAccordion">
                  {versions.map((v, idx) => {
                    const isActive = v.id === template.activeVersionId;
                    const collapseId = `ver-${v.id}`;
                    return (
                      <div className="accordion-item" key={v.id}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button py-2 ${idx === 0 ? '' : 'collapsed'}`}
                            type="button" data-bs-toggle="collapse"
                            data-bs-target={`#${collapseId}`}
                            aria-expanded={idx === 0}
                            style={{ fontSize: 13 }}
                            onClick={() => { setSelectedVersionId(v.id); setRightTab('preview'); }}
                          >
                            <div className="d-flex align-items-center gap-2 w-100 flex-wrap">
                              <span className="fw-bold">v{v.versionNumber}</span>
                              {isSuperseded(v, template.activeVersionId)
                                ? <StatusBadge status="superseded" />
                                : <StatusBadge status={v.status} />}
                              {isActive && <span className="badge bg-primary" style={{ fontSize: 10 }}>★ Active</span>}
                              <span className="text-muted ms-auto" style={{ fontSize: 11 }}>{formatDate(v.createdAt)}</span>
                            </div>
                          </button>
                        </h2>
                        <div id={collapseId} className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`}>
                          <div className="accordion-body py-2 px-3" style={{ fontSize: 12 }}>
                            <table className="table table-borderless table-sm mb-1" style={{ fontSize: 12 }}>
                              <tbody>
                                <tr>
                                  <th className="text-muted fw-normal py-1" style={{ width: 120 }}>Created</th>
                                  <td>{formatDate(v.createdAt)}{v.CreatedBy ? ` · ${v.CreatedBy.username}` : ''}</td>
                                </tr>
                                {(v.submittedAt || v.SubmittedBy) && (
                                  <tr>
                                    <th className="text-muted fw-normal py-1">Submitted</th>
                                    <td>{formatDate(v.submittedAt)}{v.SubmittedBy ? ` · ${v.SubmittedBy.username}` : ''}</td>
                                  </tr>
                                )}
                                {/* Persistent rejection record — shown even after resubmit/approve */}
                                {v.lastRejectedAt && v.status !== 'rejected' && (
                                  <>
                                    <tr style={{ color: '#dc3545' }}>
                                      <th className="fw-normal py-1" style={{ width: 120, color: '#dc3545' }}>Rejected</th>
                                      <td>{formatDate(v.lastRejectedAt)}{v.LastRejectedBy ? ` · ${v.LastRejectedBy.username}` : ''}</td>
                                    </tr>
                                    {v.lastRejectionNotes && (
                                      <tr style={{ color: '#dc3545' }}>
                                        <th className="fw-normal py-1 align-top" style={{ color: '#dc3545' }}>Rejection notes</th>
                                        <td style={{ whiteSpace: 'pre-line' }}>{v.lastRejectionNotes}</td>
                                      </tr>
                                    )}
                                  </>
                                )}
                                {(v.reviewedAt || v.ReviewedBy) && (
                                  <tr style={v.status === 'rejected' ? { color: '#dc3545' } : undefined}>
                                    <th className="fw-normal py-1" style={{ width: 120, color: v.status === 'rejected' ? '#dc3545' : undefined }}>
                                      {v.status === 'rejected' ? 'Rejected' : 'Approved'}
                                    </th>
                                    <td>{formatDate(v.reviewedAt)}{v.ReviewedBy ? ` · ${v.ReviewedBy.username}` : ''}</td>
                                  </tr>
                                )}
                                {v.changeNotes && <tr><th className="text-muted fw-normal py-1 align-top">Change notes</th><td style={{ whiteSpace: 'pre-line' }}>{v.changeNotes}</td></tr>}
                                {v.reviewNotes && <tr><th className="fw-normal py-1 align-top" style={{ color: v.status === 'rejected' ? '#dc3545' : undefined }}>Review notes</th><td style={{ whiteSpace: 'pre-line', color: v.status === 'rejected' ? '#dc3545' : undefined }}>{v.reviewNotes}</td></tr>}
                              </tbody>
                            </table>
                            <div className="d-flex gap-2 flex-wrap">
                              {hasModule('labels.approve') && v.status === 'pending_approval' && (
                                <>
                                  <button className="btn btn-success btn-sm" style={{ fontSize: 11 }}
                                    onClick={() => handleApprove(v)} disabled={approvingId === v.id}>
                                    {approvingId === v.id ? 'Approving…' : 'Approve'}
                                  </button>
                                  <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }}
                                    onClick={() => { setRejectTarget(v); setShowRejectModal(true); }}>
                                    Reject
                                  </button>
                                </>
                              )}
                              {hasModule('labels.edit') && (v.status === 'draft' || v.status === 'rejected') && (
                                <button className="btn btn-outline-warning btn-sm" style={{ fontSize: 11 }}
                                  onClick={() => handleSubmit(v)}
                                  disabled={submittingId === v.id || (template.Product?.regulatoryAgency === 'usda' && !usdaMarkPresent(v.snapshot))}
                                  title={template.Product?.regulatoryAgency === 'usda' && !usdaMarkPresent(v.snapshot) ? 'Missing the USDA Inspection Mark block.' : undefined}>
                                  {submittingId === v.id ? 'Submitting…' : 'Submit for Approval'}
                                </button>
                              )}
                              {hasModule('labels.edit') && v.status === 'draft' && (
                                <button className="btn btn-outline-danger btn-sm ms-auto" style={{ fontSize: 11 }}
                                  onClick={() => setDeleteDraftTarget(v)}>
                                  Delete draft
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Presentations (Variants) ── */}
      {hasModule('labels.edit') && (
        <div className="mt-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0" style={{ fontSize: 14 }}>Presentations</h6>
            <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 12 }}
              onClick={() => { setEditingVariant(null); setShowVariantModal(true); }}>
              + Add Presentation
            </button>
          </div>
          {(template.Variants || []).length > 0 && (
            <div className="mb-2 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
              <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>Base name:</span>
              <input
                className="form-control form-control-sm"
                style={{ maxWidth: 220, fontSize: 12 }}
                defaultValue={template.baseVariantName || ''}
                placeholder="Base Label"
                onBlur={async e => {
                  const val = e.target.value.trim() || null;
                  if (val === (template.baseVariantName || null)) return;
                  try { await productLabelService.updateTemplate(template.id, { baseVariantName: val }); load(); }
                  catch { /* silent */ }
                }}
              />
              <span className="text-muted" style={{ fontSize: 11 }}>
                shown in print picker when no presentation override is selected
              </span>
            </div>
          )}
          {(template.Variants || []).length === 0 ? (
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              No presentations defined — label prints as a single presentation.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Label Title</th>
                    <th>Net Weight</th>
                    <th>UPC</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(template.Variants || []).map(v => (
                    <tr key={v.id}>
                      <td className="fw-semibold">
                        {v.name}
                        {v.isDefault && <span className="badge bg-primary ms-1" style={{ fontSize: 9 }}>default</span>}
                      </td>
                      <td className="text-muted">{v.labelTitle || '—'}</td>
                      <td className="text-muted">{v.netWeight || '—'}</td>
                      <td className="text-muted">{v.upc || '—'}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline-secondary btn-sm me-1" style={{ fontSize: 11 }}
                          onClick={() => { setEditingVariant(v); setShowVariantModal(true); }}>
                          Edit
                        </button>
                        <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 11 }}
                          onClick={async () => {
                            if (!window.confirm(`Delete presentation "${v.name}"?`)) return;
                            try {
                              await productLabelService.deleteVariant(template.id, v.id);
                              load();
                            } catch {
                              setActionError('Failed to delete presentation.');
                            }
                          }}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <VariantModal
        show={showVariantModal}
        onClose={() => { setShowVariantModal(false); setEditingVariant(null); }}
        templateId={template?.id}
        variant={editingVariant}
        onSaved={() => { setShowVariantModal(false); setEditingVariant(null); load(); }}
      />

      <RejectModal
        show={showRejectModal}
        onClose={() => { setShowRejectModal(false); setRejectTarget(null); }}
        onConfirm={handleRejectConfirm}
        loading={rejectLoading}
      />

      <NewVersionModal
        show={showNewVersionModal}
        onClose={() => setShowNewVersionModal(false)}
        template={template}
        onCreated={handleNewVersionCreated}
      />

      <PrintModal
        show={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        template={template}
        variants={template?.Variants || []}
        onConfirm={handlePrintConfirm}
        confirming={printConfirming}
        confirmError={printError}
        prefillOrderId={prefillOrderId}
      />

      {/* Delete confirmation */}
      {deleteDraftTarget && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 400 }}>
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title text-danger">Delete Draft Version</h5>
                </div>
                <div className="modal-body pt-2">
                  <p className="mb-1">Delete <strong>v{deleteDraftTarget.versionNumber}</strong> of this label?</p>
                  <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                    This action cannot be undone.
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={() => setDeleteDraftTarget(null)} disabled={deletingDraft}>
                    Cancel
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleDeleteDraftVersion} disabled={deletingDraft}>
                    {deletingDraft
                      ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Deleting…</>
                      : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showDeleteModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title text-danger">Delete Label</h5>
                </div>
                <div className="modal-body pt-2">
                  <p className="mb-1">Are you sure you want to delete:</p>
                  <p className="fw-semibold mb-3" style={{ fontSize: 15 }}>"{template.name}"</p>
                  <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                    This will permanently delete the label and all its draft versions. This action cannot be undone.
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                    Cancel
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                    {deleting
                      ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Deleting…</>
                      : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Print area — portal onto body so #root can be hidden during print ── */}
      {printJob && activeVersion?.snapshot && (() => {
        const { design, content } = splitSnapshot(activeVersion.snapshot);
        const expLabel = resolveExpLabel(template);
        const dateFmt = printJob.dateFormat || template.dateFormat || 'MM/DD/YYYY';
        const expDateFmt = printJob.expirationDate ? formatLabelDate(printJob.expirationDate, dateFmt) : '';
        const prodDateFmt = printJob.productionDate ? formatLabelDate(printJob.productionDate, dateFmt) : '';
        // Resolve the selected variant (packaging layer — never overrides regulatory fields)
        const variant = printJob.variantId != null
          ? (template.Variants || []).find(v => Number(v.id) === Number(printJob.variantId))
          : null;
        // Effective expiration label: variant can override the template-level setting
        const effectiveExpLabel = (variant?.expLabelOverride?.trim()) || expLabel;
        const baseContent = {
          ...content,
          ...(companyLogoUrl && { logoUrl: companyLogoUrl }),
          // Variant packaging overrides — only non-regulatory fields
          ...(variant?.labelTitle    != null && variant.labelTitle    !== '' && { productName: variant.labelTitle }),
          ...(variant?.netWeight     != null && variant.netWeight     !== '' && { netWeight: variant.netWeight }),
          // If a variant is selected: use its UPC if set, otherwise blank it (don't inherit base label's UPC)
          ...(variant != null && (variant.upc ? { upc: variant.upc, caseUpc: variant.upc } : { upc: '', caseUpc: '' })),
          // Same rule for PLU/SKU — the customer's own product code, fixed per label content
          // (content.pluSku, already in the ...content spread above); a selected Variant can
          // override it, e.g. when the same product has multiple pack sizes with different PLUs.
          ...(variant != null && { pluSku: variant.pluSku || '' }),
          ...(variant?.logoOverride  != null && variant.logoOverride  !== '' && { logoUrl: variant.logoOverride }),
          // Date display overrides
          ...(variant?.prodDateDisplay === 'show' && { showProdDate: true }),
          ...(variant?.prodDateDisplay === 'hide' && { showProdDate: false }),
          ...(variant?.prodDateLabel   != null && variant.prodDateLabel !== '' && { prodDateLabel: variant.prodDateLabel }),
          // expBlockTitle: pass through even if empty string (empty = hide the title row)
          ...(variant != null && variant.expBlockTitle != null && { expBlockTitle: variant.expBlockTitle }),
          ...(variant?.manufacturerInfoOverride?.trim() && { manufacturerInfo: variant.manufacturerInfoOverride }),
          ...(variant?.customTextOverride?.trim() && { customTextOverride: variant.customTextOverride }),
          expirationLabel: effectiveExpLabel + ':',
          lot: printJob.lotCode ? `Lot: ${printJob.lotCode}` : '',
          exp: expDateFmt ? `${effectiveExpLabel}: ${expDateFmt}` : '',
          prodDate: prodDateFmt,
          po: printJob.poNumber || '',
          productId: template.productId || null,
          // GS1-128 case barcode inputs — weight and best-by are the same for every copy in this
          // print job; the serial (AI 21) is per-copy, computed below in the render loop.
          // Weight: content.caseNetWeightLb is the base value (works with no Variant at all —
          // see docs/PRODUCT_LABELS_MODULE.md); a selected Variant overrides it, same pattern as
          // caseUpc/upc above, including "blank if variant selected but its own weight isn't set"
          // rather than silently falling back to the base value.
          gs1CaseWeightLb: variant != null
            ? (variant.caseNetWeightLb ?? null)
            : (content.caseNetWeightLb ? parseFloat(content.caseNetWeightLb) : null),
          gs1BestByISO: printJob.expirationDate || null,
        };
        const w = design?.width  || 6;
        const h = design?.height || 4;
        const copies = Math.max(1, printJob.copies || 1);
        return ReactDOM.createPortal(
          <>
            <style>{`
              @media print {
                #root { display: none !important; }
                #label-print-portal { display: block !important; }
                @page { margin: 0; size: ${w}in ${h}in; }
              }
            `}</style>
            <div id="label-print-portal" style={{ display: 'none' }}>
              {Array.from({ length: copies }, (_, i) => {
                const printContent = { ...baseContent, gs1Serial: buildGs1Serial(printJob.lotCode, i + 1) };
                return (
                  <div key={i} style={{ pageBreakAfter: i < copies - 1 ? 'always' : 'auto', width: `${w}in`, height: `${h}in`, overflow: 'hidden' }}>
                    <LabelRenderer design={design} content={printContent} scale={1} isDesign={false} />
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        );
      })()}
    </div>
  );
}

export default LabelDetailPage;
